// Configuración multi-país. Cada locale incluye contenido nativo localizado y
// referencias verificables al producto real (extraídas de `/Geovis/{country}/src/core/config/layerGroups.ts`).
//
// IMPORTANTE para hreflang:
//   1. Auto-referencia: cada locale aparece en su propio bloque.
//   2. Reciprocidad: si X apunta a Y, Y apunta a X.
//   3. Canonical único por locale, jamás cross-locale.

const BASE_URL = 'https://www.geovis.es';

/* eslint-disable max-lines */
export const LOCALES = {
  // ─────────────────────────────────────────────────────────────────────────
  es: {
    code: 'es',
    hreflang: 'es-ES',
    path: '/',
    flag: '🇪🇸',
    countryName: 'España',
    countryNative: 'España',
    languageLabel: 'Español',
    coverageStatus: 'completa',
    modulesCount: 50,
    title: 'GeoVis · Visor GIS para análisis territorial y viabilidad de proyectos | Catastro + SIGPAC + Red Natura',
    description:
      'Visor GIS web para análisis territorial profesional: urbanismo, due diligence, viabilidad de proyectos, consultoría ambiental, ingeniería, renovables y promoción de suelo. Cruza Catastro, SIGPAC, Red Natura, planeamiento y +40 capas oficiales. Informe PDF en menos de 24h.',
    badge: '7 días gratis · Sin tarjeta',
    h1Lead: 'Análisis territorial y',
    h1Highlight: 'viabilidad de proyectos',
    h1Tail: 'en un solo visor',
    subtitle:
      'Visor GIS con Catastro, SIGPAC, IGN, planeamiento autonómico, Red Natura, red eléctrica REE y +40 capas oficiales. Para urbanismo, due diligence territorial, ingeniería, ambiental, renovables y promoción inmobiliaria.',
    coverageBlurb:
      'Cobertura nacional completa: península, Canarias, Baleares, Ceuta y Melilla. Sincronización con fuentes oficiales y trazabilidad por capa. El visor unifica datos de Catastro, IGN (PNOA, MDT), SIGPAC, IDEs autonómicas, MITECO, REE, AESA y patrimonio cultural en un mismo mapa con herramientas de cruce, isócronas, búsqueda de POIs y exportación profesional.',
    sectors: [
      { name: 'Urbanismo y planeamiento', body: 'Calificación del suelo, normativa urbanística autonómica y municipal, BIC y servidumbres.' },
      { name: 'Due diligence territorial', body: 'Cribado de carteras de suelo, identificación de afecciones críticas antes de cerrar opciones.' },
      { name: 'Renovables (FV utility · eólica)', body: 'Red Natura, capacidad de acceso REE, MUP, vías pecuarias y servidumbres aero.' },
      { name: 'Consultoría ambiental', body: 'Cruce de capas para estudios de impacto ambiental y declaraciones responsables.' },
      { name: 'Ingeniería civil', body: 'Trazado, servidumbres, dominio público hidráulico y costero.' },
      { name: 'Promoción inmobiliaria', body: 'Calificación urbanística, planeamiento vigente, parcelario.' },
      { name: 'Agricultura', body: 'SIGPAC con recintos, usos, pendiente y elegibilidad PAC.' },
      { name: 'Administración pública', body: 'Evaluación técnica de iniciativas y solicitudes con trazabilidad documental.' },
    ],
    sourcesHeading: 'Fuentes oficiales integradas',
    sources: [
      { name: 'Sede Electrónica del Catastro', desc: 'Parcelas, titularidad, referencias catastrales con cobertura nacional.' },
      { name: 'IGN — Instituto Geográfico Nacional', desc: 'PNOA, MDT, cartografía base, división administrativa.' },
      { name: 'SIGPAC — FEGA', desc: 'Recintos agrarios con uso, pendiente y elegibilidad PAC.' },
      { name: 'MITECO', desc: 'Red Natura 2000 (ZEC + ZEPA), montes de utilidad pública, dominio público hidráulico.' },
      { name: 'REE — Red Eléctrica de España', desc: 'Líneas AT/MAT, subestaciones, capacidad de acceso publicada por nudo.' },
      { name: 'AESA', desc: 'Servidumbres aeronáuticas civiles y zonas de protección.' },
      { name: 'IDEs autonómicas', desc: 'Planeamiento urbanístico autonómico y municipal sincronizado.' },
      { name: 'Patrimonio cultural', desc: 'BIC, zonas arqueológicas y protección estatal/autonómica.' },
    ],
    modules: {
      cadastral: {
        label: 'Catastro y división administrativa',
        items: [
          'Parcelas catastrales — Sede Electrónica del Catastro (cobertura nacional)',
          'Límites municipales, provinciales, autonómicos',
          'División de zonas urbanas/rústicas',
        ],
      },
      urbanismo: {
        label: 'Urbanismo y planeamiento',
        items: [
          'Planeamiento autonómico y municipal vigente',
          'Calificación del suelo (urbano, urbanizable, no urbanizable)',
          'BIC, zonas de protección patrimonial',
          'Catastro minero',
        ],
      },
      territorial: {
        label: 'Suelo, riesgos y energías renovables',
        items: [
          'SIGPAC — recintos agrarios, usos, pendiente, elegibilidad PAC',
          'SIOSE — Sistema de Información sobre Ocupación del Suelo',
          'Geología, hidrocarburos, zonas inundables (ARPSI)',
          'Renovables en tramitación — proyectos en cola administrativa',
        ],
      },
      infrastructure: {
        label: 'Infraestructura',
        items: [
          'Red eléctrica REE — líneas + subestaciones + capacidad de acceso',
          'Carreteras y ferrocarriles',
          'Puertos y aeropuertos',
          'Servidumbres aeronáuticas civiles + militares',
          'Redes de telecomunicación',
          'Antenas y demanda eléctrica',
        ],
      },
      environment: {
        label: 'Ambiente y patrimonio',
        items: [
          'Red Natura 2000 (ZEC + ZEPA)',
          'Montes de Utilidad Pública (MUP)',
          'Vías pecuarias',
          'Protección costera y posidonia oceánica',
          'Patrimonio cultural — BIC nacional y autonómico',
          'Zonificación acústica',
          'Incendios forestales',
        ],
      },
      hydrography: {
        label: 'Hidrografía y aguas',
        items: [
          'Red hidrográfica — cauces, ríos, masas de agua',
          'Zonas inundables ARPSI (peligrosidad T100 / T500)',
          'Dominio público hidráulico',
          'Acuíferos',
        ],
      },
    },
    restrictionsHeading: 'Restricciones bloqueantes detectadas',
    restrictions: [
      { name: 'Red Natura 2000 (ZEC + ZEPA)', body: 'Bloquea o condiciona FV utility y eólica en función del plan de gestión.' },
      { name: 'Montes de Utilidad Pública (MUP)', body: 'Requiere ocupación o desafectación tramitada con la administración forestal.' },
      { name: 'Vías pecuarias', body: 'Servidumbres de tránsito ganadero protegidas por Ley 3/1995.' },
      { name: 'Zonas inundables (ARPSI)', body: 'Peligrosidad T100/T500 afecta seguros y financiación bancaria.' },
      { name: 'Servidumbres aeronáuticas', body: 'Limitaciones de altura y obstáculos cerca de aeródromos civiles y militares.' },
      { name: 'Patrimonio cultural y BIC', body: 'Zonas arqueológicas y entornos de protección regional/estatal.' },
      { name: 'Dominio público hidráulico', body: '5 m de servidumbre + 100 m de policía afecta proyectos lineales.' },
      { name: 'Capacidad de acceso REE', body: 'Sin capacidad publicada en el nudo más cercano, el proyecto no se conecta.' },
    ],
    regulatoryFramework: [
      { norma: 'Ley 42/2007', scope: 'Patrimonio Natural y Biodiversidad — Red Natura 2000.' },
      { norma: 'RD Legislativo 7/2015', scope: 'Texto Refundido de la Ley del Suelo y Rehabilitación Urbana.' },
      { norma: 'Ley 21/2013', scope: 'Evaluación ambiental (estratégica y de proyectos).' },
      { norma: 'Ley 24/2013', scope: 'Sector eléctrico — autorización y capacidad de acceso.' },
      { norma: 'Ley 3/1995', scope: 'Vías Pecuarias.' },
      { norma: 'RDL 1/2001 (TR Aguas)', scope: 'Dominio público hidráulico, servidumbre, policía.' },
    ],
    faq: [
      { q: '¿GeoVis sustituye a QGIS o ArcGIS?', a: 'No. GeoVis es un visor de cribado y due diligence con las capas oficiales precargadas y sincronizadas. QGIS y ArcGIS siguen siendo necesarios para geoprocesamiento avanzado, raster pesado o ETL personalizado. La mayoría de equipos usan GeoVis para el cribado rápido y el informe, y QGIS solo para el detalle final.' },
      { q: '¿Con qué frecuencia se actualizan las capas?', a: 'La sincronización es continua con cada fuente oficial. Catastro y SIGPAC se actualizan en función de los ciclos de publicación; planeamiento autonómico cuando la IDE de cada CCAA publica; REE cuando publica nuevos nudos. Cada capa muestra su última fecha de actualización.' },
      { q: '¿Puedo exportar lo que veo en el mapa?', a: 'Sí, en plan Pro y Premium: KMZ, GeoJSON, Shapefile y PDF. El PDF de informe pay-per-use incluye 88 capas cruzadas con el perímetro y marco normativo aplicable.' },
      { q: '¿Cobertura completa de las 17 CCAA?', a: 'Sí. Península, Canarias, Baleares, Ceuta y Melilla. Más de 50 módulos de capas oficiales sincronizadas con las IDE autonómicas y los ministerios.' },
      { q: '¿Sirve para urbanismo y promoción inmobiliaria?', a: 'Sí. Es uno de los casos de uso principales: calificación del suelo, planeamiento vigente, parcelario catastral y zonas de protección patrimonial en un solo mapa.' },
      { q: '¿Cómo funciona el cálculo del informe pay-per-use?', a: '99 € base (hasta 10 ha) + 2 €/ha extra (Premium: 1 €/ha). Tope 999 €. Premium incluye 1 informe gratis al mes hasta 100 ha. Entrega en menos de 24 horas.' },
    ],
    glossary: [
      { term: 'BIC', definition: 'Bien de Interés Cultural — máxima categoría de protección patrimonial estatal.' },
      { term: 'ZEC / ZEPA', definition: 'Zonas de Especial Conservación / Especial Protección para las Aves (Red Natura 2000).' },
      { term: 'MUP', definition: 'Monte de Utilidad Pública — suelo forestal con protección administrativa especial.' },
      { term: 'ARPSI', definition: 'Áreas con Riesgo Potencial Significativo de Inundación (Directiva 2007/60/CE).' },
      { term: 'SIGPAC', definition: 'Sistema de Información Geográfica de Parcelas Agrícolas — recintos de la PAC.' },
      { term: 'PNOA', definition: 'Plan Nacional de Ortofotografía Aérea — cobertura ortofoto de España.' },
      { term: 'IDE', definition: 'Infraestructura de Datos Espaciales — IDE Nacional, IDE autonómicas, IDE municipales.' },
      { term: 'Capacidad de acceso', definition: 'MW disponibles por nudo de la red de transporte publicados por REE.' },
    ],
    legalReference: 'Marco aplicable: Ley 42/2007 (Patrimonio Natural y Biodiversidad), Ley 21/2013 (evaluación ambiental), Ley 24/2013 (sector eléctrico), TR Ley Suelo (RDL 7/2015), Ley 3/1995 (vías pecuarias), Real Decreto-Legislativo 1/2001 (TR Aguas), normativa autonómica.',
    ctaPrimary: 'Empezar gratis',
    ctaSecondary: 'Solicitar demo',
    signupUrl: 'https://aplicacion.geovis.es/auth/signup?source=landing_es',
  },

  // ─────────────────────────────────────────────────────────────────────────
  pt: {
    code: 'pt',
    hreflang: 'pt-PT',
    path: '/pt',
    flag: '🇵🇹',
    countryName: 'Portugal',
    countryNative: 'Portugal',
    languageLabel: 'Português',
    coverageStatus: 'madura',
    modulesCount: 45,
    title: 'GeoVis Portugal · Visualizador GIS para análise territorial e viabilidade de projetos | CAOP + REN + Natura 2000',
    description:
      'Visualizador GIS para análise territorial em Portugal: urbanismo, due diligence, projetos renováveis, ambiente e engenharia. Cruza CAOP, cadastro predial BUPi, Rede Natura 2000, REN, RAN, iSIP, COS, riscos de inundação e mais de 30 camadas oficiais.',
    badge: '7 dias grátis · Sem cartão',
    h1Lead: 'Análise territorial e',
    h1Highlight: 'viabilidade de projetos',
    h1Tail: 'em Portugal',
    subtitle:
      'Cruze CAOP, cadastro predial (DGT BDNCP, ~1,94 M parcelas), Rede Natura 2000, REN, RAN, iSIP, COS, recursos minerais DGEG, áreas ardidas e zonas inundáveis num único visualizador GIS profissional.',
    coverageBlurb:
      'Cobertura nacional: continente, Açores e Madeira. 45 módulos de camadas oficiais sincronizadas com DGT, APA, DGEG, ICNF, IFAP e operadores de transporte. Inclui cadastro predial nacional (BDNCP 1,94 M) e Madeira, planeamento territorial, REN/RAN, conservação e infraestrutura energética.',
    sectors: [
      { name: 'Urbanismo e PDM', body: 'Planos Diretores Municipais, regime de uso do solo e instrumentos de gestão territorial.' },
      { name: 'Due diligence territorial', body: 'Triagem de terrenos antes de promessas de compra. Identificação rápida de REN, RAN e Rede Natura.' },
      { name: 'Renováveis (FV · eólica)', body: 'Tramitação renováveis, capacidade de subestações, atlas eólico, condicionantes ambientais.' },
      { name: 'Ambiente e ordenamento', body: 'Cruzamento com Rede Natura, habitats, áreas ardidas, ZIF, conservação.' },
      { name: 'Engenharia civil', body: 'Hidrografia, declives, geologia, recursos minerais para projetos lineares.' },
      { name: 'Agricultura', body: 'iSIP (parcelário PAC IFAP) e COS para uso agrícola e elegibilidade.' },
    ],
    sourcesHeading: 'Fontes oficiais integradas',
    sources: [
      { name: 'DGT — Direção-Geral do Território', desc: 'CAOP, cadastro predial BDNCP (1,94 M parcelas), COS 2023, cartografia base.' },
      { name: 'APA — Agência Portuguesa do Ambiente', desc: 'Recursos hídricos, riscos de inundação, ambiente.' },
      { name: 'DGEG', desc: 'Energia: rede elétrica, projetos em tramitação, recursos minerais e geotérmicos.' },
      { name: 'ICNF', desc: 'Rede Natura 2000, áreas protegidas, ZIF, áreas ardidas, conservação da natureza.' },
      { name: 'IFAP', desc: 'iSIP — Sistema de Identificação de Parcelário (PAC).' },
      { name: 'IGeoE', desc: 'Cartografia militar e geodesia.' },
      { name: 'REN — operador de transporte', desc: 'Rede de transporte de eletricidade e gás.' },
    ],
    modules: {
      cadastral: {
        label: 'Cadastro e limites administrativos',
        items: [
          'CAOP — Carta Administrativa Oficial de Portugal (DGT)',
          'Cadastro predial BDNCP — DGT + Madeira (1,94 M parcelas)',
          'Cobertura de cadastro continental e regional',
        ],
      },
      urbanismo: {
        label: 'Urbanismo e planeamento',
        items: [
          'CRUS — Carta de Regime de Uso do Solo (PDM nacional)',
          'Instrumentos de Gestão Territorial (IGT)',
        ],
      },
      territorial: {
        label: 'Cadastro rural e usos do solo',
        items: [
          'iSIP — parcelário PAC (IFAP)',
          'COS 2023 — Carta de Ocupação do Solo (DGT)',
          'Recursos minerais e energéticos (DGEG)',
          'RAN — Reserva Agrícola Nacional',
          'Renováveis em tramitação (DGEG)',
          'Declives, vertentes, geologia',
        ],
      },
      infrastructure: {
        label: 'Infraestrutura',
        items: [
          'Rede rodoviária (IP — RRN)',
          'Rede ferroviária',
          'Rede elétrica + subestações + capacidade',
          'Centrais e infraestrutura energética',
          'Atlas eólico nacional',
          'Hidrocarbonetos — concessões e poços',
        ],
      },
      environment: {
        label: 'Ambiente e conservação',
        items: [
          'Rede Natura 2000 (SIC + ZPE) — ICNF',
          'REN — Reserva Ecológica Nacional',
          'Áreas protegidas nacionais',
          'Habitats e conservação',
          'Áreas ardidas (ICNF)',
          'ZIF — Zonas de Intervenção Florestal',
          'Imóveis classificados (Património)',
          'POAAP, POC, POEM (planos costeiros e do mar)',
        ],
      },
      hydrography: {
        label: 'Hidrografia',
        items: [
          'Rede hidrográfica',
          'Massas de água (DCE)',
          'Zonas inundáveis',
          'Hidrogeologia',
          'SRUPS hídricas e de defesa',
        ],
      },
    },
    restrictionsHeading: 'Restrições detetadas',
    restrictions: [
      { name: 'Rede Natura 2000 (SIC + ZPE)', body: 'Condiciona ou bloqueia projetos consoante a tipologia.' },
      { name: 'REN — Reserva Ecológica Nacional', body: 'Restrições a edificação e movimentação de terras.' },
      { name: 'RAN — Reserva Agrícola Nacional', body: 'Limita afetação de solos com elevada aptidão agrícola.' },
      { name: 'Servidões aeronáuticas e militares (SRUPS-Defesa)', body: 'Limitações de altura e zonas de proteção.' },
      { name: 'Áreas inundáveis e domínio hídrico', body: 'Risco hidráulico para licenciamento bancário.' },
      { name: 'Património classificado', body: 'Imóveis e respetivas zonas de proteção.' },
      { name: 'POAAP / POC / POEM', body: 'Planos de albufeira, costeira e marítimo — restrições específicas.' },
    ],
    regulatoryFramework: [
      { norma: 'Decreto-Lei 151-B/2013', scope: 'Avaliação de Impacte Ambiental (AIA).' },
      { norma: 'Decreto-Lei 142/2008', scope: 'Rede Nacional de Áreas Protegidas (RNAP).' },
      { norma: 'RJUE (DL 555/99)', scope: 'Regime Jurídico da Urbanização e Edificação.' },
      { norma: 'Decreto-Lei 169/2001', scope: 'REN — Reserva Ecológica Nacional.' },
      { norma: 'Decreto-Lei 73/2009', scope: 'RAN — Reserva Agrícola Nacional.' },
      { norma: 'Decreto-Lei 76/2019', scope: 'Renováveis e produção descentralizada.' },
    ],
    faq: [
      { q: 'O GeoVis cobre o cadastro predial em todo o país?', a: 'A cobertura do cadastro predial BDNCP é nacional (~1,94 M parcelas continentais) acrescida de Madeira. A migração para o sistema cadastral simplificado BUPi está em curso e refletimos as áreas já publicadas pela DGT.' },
      { q: 'Posso fazer due diligence de REN/RAN antes de comprar um terreno?', a: 'Sim. Esse é um dos principais casos de uso: cruzar o perímetro com Rede Natura 2000, REN, RAN, áreas ardidas e zonas inundáveis em segundos.' },
      { q: 'Quanto custa o relatório PDF?', a: '99 € base (até 10 ha) + 2 €/ha extra. Premium: 1 €/ha extra e 1 relatório/mês grátis até 100 ha. Tope máximo 999 €.' },
      { q: 'A informação está atualizada?', a: 'Sim. Sincronização contínua com DGT, ICNF, APA, DGEG, IFAP. Cada camada mostra a data de última publicação oficial.' },
      { q: 'Faz sentido para renováveis em Portugal?', a: 'Sim. Inclui tramitação de renováveis, atlas eólico, subestações com capacidade e cruzamento com REN/Natura — o trio crítico para FV utility e eólica.' },
    ],
    glossary: [
      { term: 'CAOP', definition: 'Carta Administrativa Oficial de Portugal — limites publicados pela DGT.' },
      { term: 'BDNCP', definition: 'Base de Dados Nacional do Cadastro Predial — DGT.' },
      { term: 'REN', definition: 'Reserva Ecológica Nacional — restrição territorial nacional.' },
      { term: 'RAN', definition: 'Reserva Agrícola Nacional — proteção de solos agrícolas.' },
      { term: 'iSIP', definition: 'Identificação Sistema Parcelário (IFAP) — equivalente português ao SIGPAC.' },
      { term: 'COS', definition: 'Carta de Ocupação do Solo — DGT.' },
      { term: 'ZIF', definition: 'Zona de Intervenção Florestal — gestão florestal coletiva.' },
      { term: 'PDM', definition: 'Plano Diretor Municipal — instrumento de gestão territorial.' },
    ],
    legalReference: 'Quadro aplicável: DL 151-B/2013 (AIA), DL 142/2008 (RNAP), RJUE (DL 555/99), DL 169/2001 (REN), DL 73/2009 (RAN), DL 76/2019 (renováveis).',
    ctaPrimary: 'Experimentar grátis',
    ctaSecondary: 'Pedir demonstração',
    signupUrl: 'https://aplicacion.geovis.es/auth/signup?source=landing_pt',
  },

  // ─────────────────────────────────────────────────────────────────────────
  cl: {
    code: 'cl',
    hreflang: 'es-CL',
    path: '/cl',
    flag: '🇨🇱',
    countryName: 'Chile',
    countryNative: 'Chile',
    languageLabel: 'Español (Chile)',
    coverageStatus: 'madura',
    modulesCount: 47,
    title: 'GeoVis Chile · Visor GIS para análisis territorial y SEIA | Catastro SII + MINVU IPT + SNASPE',
    description:
      'Visor GIS profesional para análisis territorial en Chile: SEIA, IPT (PRC/PRI/PRMS), catastro MINVU/CIREN, SNASPE, glaciares DGA, sistema eléctrico CNE, sitios prioritarios MMA. Compatible con Ley 19.300 y DS 40/2012.',
    badge: '7 días gratis · Sin tarjeta',
    h1Lead: 'Análisis territorial y',
    h1Highlight: 'evaluación ambiental',
    h1Tail: 'en Chile',
    subtitle:
      'Cruza catastro predial MINVU (~2,5M predios urbanos + 14 regiones rurales CIREN), IPT MINVU (PRC/PRI/PRMS), SEIA SEA, SNASPE CONAF, glaciares DGA, sistema eléctrico CNE y +40 capas oficiales. Para due diligence, renovables, minería, urbanismo y consultoría ambiental.',
    coverageBlurb:
      'Cobertura nacional Atacama a Magallanes. 47 módulos de capas oficiales: catastro MINVU/CIREN, IPT (Instrumentos de Planificación Territorial), SEIA con ~30 mil DIA+EIA, SNASPE CONAF, glaciares DGA (IPG 2022, 26 mil polígonos), bosque nativo CONAF (13,7M rodales), red eléctrica CNE, recursos solar/eólico (Global Solar Atlas + Global Wind Atlas), DAA SNIA y sitios prioritarios MMA.',
    sectors: [
      { name: 'Renovables (FV utility · eólica · BESS)', body: 'Capacidad SEN, recurso solar/eólico, SEIA, sitios prioritarios MMA, glaciares.' },
      { name: 'Evaluación ambiental SEIA', body: 'Cruce con proyectos previos, RCAs aprobadas, áreas sensibles y comunidades.' },
      { name: 'Minería y due diligence', body: 'Cruce con SEIA, SNASPE, glaciares, salares, relaves SERNAGEOMIN.' },
      { name: 'Urbanismo (PRC/PRI/PRMS)', body: 'IPT vigentes, límites urbanos MINVU, patrimonio ICH+ZCH.' },
      { name: 'Forestal y agroindustria', body: 'Bosque Nativo CONAF, capacidad de uso CIREN, áreas ardidas.' },
      { name: 'Consultoría territorial pública', body: 'Análisis técnico de instrumentos de planificación y proyectos en tramitación.' },
    ],
    sourcesHeading: 'Fuentes oficiales integradas',
    sources: [
      { name: 'SII / CBR', desc: 'Roles, avalúos y propiedad predial (vía MINVU IPT y CIREN).' },
      { name: 'MINVU IPT', desc: 'Planes Reguladores Comunales/Intercomunales/Metropolitanos, límites urbanos (598 polígonos).' },
      { name: 'SEA — SEIA', desc: '~30k proyectos DIA + EIA con estado y coordenadas (Ley 19.300).' },
      { name: 'CIREN', desc: 'Capacidad de uso (~91k polígonos), erosión, predios rurales 14 regiones.' },
      { name: 'CONAF', desc: 'SNASPE (parques, reservas), bosque nativo (13,7M rodales · Ley 20.283), incendios.' },
      { name: 'DGA — Dirección General de Aguas', desc: 'Glaciares (IPG 2022, 26k polígonos), acuíferos SHAC, DAA SNIA (~76k derechos).' },
      { name: 'CNE — Comisión Nacional de Energía', desc: 'Sistema eléctrico nacional, líneas, subestaciones, centrales.' },
      { name: 'SERNAGEOMIN', desc: 'Volcanes RNVV (87 activos), relaves (836 polígonos), remoción en masa (18,4k eventos).' },
      { name: 'MMA — Ministerio del Medio Ambiente', desc: 'Sitios prioritarios biodiversidad, humedales SIMBIO (118k + 138 urbanos Ley 21.202), zonas saturadas/latentes.' },
      { name: 'SUBDERE / BCN', desc: 'División político-administrativa, áreas pobladas.' },
    ],
    modules: {
      cadastral: {
        label: 'Catastro y división administrativa',
        items: [
          'DPA SUBDERE — regiones, provincias, comunas',
          'Áreas pobladas BCN — ciudades, pueblos, aldeas',
          'Predios urbanos MINVU — ~150 comunas · ~2,5M predios',
          'Predios rurales CIREN — 14 regiones con rol SII',
          'Bienes Nacionales Protegidos SBAP/MBN — 56 sitios · 506k ha',
        ],
      },
      urbanismo: {
        label: 'IPT — Instrumentos de Planificación Territorial',
        items: [
          'PRC zonas normativas — 16 regiones (UPERM/UPROH)',
          'PRI · PRMS · PREMVAL · PRMC · PRDU — zonificación intercomunal y metropolitana',
          'Límites urbanos MINVU — 598 polígonos',
          'Patrimonio IPT — 4.129 inmuebles + 267 ZCH',
        ],
      },
      territorial: {
        label: 'Suelo, riesgos y renovables',
        items: [
          'Capacidad de uso CIREN — ~91k polígonos clase I-VIII (Atacama → Magallanes)',
          'Erosión nacional CIREN 2010 — ~89k polígonos',
          'Áreas de riesgo IPT MINVU',
          'Peligro volcánico SERNAGEOMIN RNVV — 87 volcanes activos',
          'CITSU tsunami SHOA — 74 cartas costeras',
          'Zonificación sísmica NCh433 — 345 comunas zonas 1-3',
          'Remoción en masa — 18.406 eventos SERNAGEOMIN',
          'Incendios forestales CONAF — 781 megaincendios 2013-2025',
          'Relaves SERNAGEOMIN — 836 polígonos',
          'Salares del norte — 19 envolventes',
          'Recurso solar Global Solar Atlas (GHI + PVOUT 1 km)',
          'Recurso eólico Global Wind Atlas (viento 100 m, 1 km)',
          'Proyectos SEIA — ~30k DIA+EIA con estado',
        ],
      },
      infrastructure: {
        label: 'Infraestructura',
        items: [
          'Red vial MOP',
          'Red ferroviaria BCN/EFE',
          'Sistema eléctrico CNE — líneas + subestaciones + centrales',
          'Combustible (ductos, terminales)',
          'Aeropuertos y aeródromos DGAC',
          'Obras portuarias DOP — 644 puntos',
          'PTAS sanitarias RETC-SINADER — 152 establecimientos',
        ],
      },
      environment: {
        label: 'Ambiente y comunidades',
        items: [
          'SNASPE CONAF + santuarios MMA',
          'Sitios Ramsar — 13 humedales internacionales',
          'AMCP-MU SUBPESCA/MMA — 15 áreas marinas',
          'Bosque Nativo CONAF — 13,7M rodales (Maule → Aysén · Ley 20.283)',
          'Monumentos Nacionales CMN',
          'ECMPO Lafkenche SUBPESCA/CONADI',
          'Áreas Desarrollo Indígena CONADI — Ley 19.253 (10 áreas)',
          'Sitios prioritarios MMA (pesan en SEIA)',
          'Zonas saturadas/latentes MMA (bloquean industria nueva)',
        ],
      },
      hydrography: {
        label: 'Hidrografía y derechos de agua',
        items: [
          'Red hidrográfica BCN/IGM 1:50k',
          'Humedales MMA SIMBIO — 118k inventario + 138 urbanos (Ley 21.202)',
          'Cuencas hidrográficas — 139/489/1.672 niveles',
          'Acuíferos SHAC DGA — 99 sectores hidrogeológicos',
          'Glaciares IPG 2022 DGA — 26k polígonos',
          'Derechos aprovechamiento DAA SNIA — ~76k DAA (actualización diaria)',
          'Inundación DOH-MOP SIALL — 429 eventos + 148 zonas',
          'Restricción/prohibición DGA aguas subterráneas',
        ],
      },
    },
    restrictionsHeading: 'Restricciones detectadas',
    restrictions: [
      { name: 'SNASPE y santuarios MMA', body: 'Bloquean intervenciones que requieren EIA por afectación directa.' },
      { name: 'Glaciares y áreas de protección hídrica DGA', body: 'Inventario IPG 2022 — 26k polígonos. Bloqueante para minería en cordillera.' },
      { name: 'IPT municipales (PRC/PRI/PRMS)', body: 'Determinan uso de suelo y áreas de riesgo a nivel comunal e intercomunal.' },
      { name: 'Sitios prioritarios MMA', body: 'Su afectación se pondera fuertemente en el SEIA.' },
      { name: 'Patrimonio CMN', body: 'Monumentos nacionales con zona de protección obligatoria.' },
      { name: 'Áreas de Desarrollo Indígena (ADI)', body: 'Ley 19.253 + Convenio 169 OIT — proceso de consulta.' },
      { name: 'Zonas saturadas/latentes MMA', body: 'Bloquean nueva industria emisora hasta planes de descontaminación.' },
      { name: 'Servidumbres SEN', body: 'Trazado de líneas y subestaciones del sistema eléctrico nacional.' },
    ],
    regulatoryFramework: [
      { norma: 'Ley 19.300', scope: 'Bases Generales del Medio Ambiente — SEIA.' },
      { norma: 'D.S. 40/2012 RSEIA', scope: 'Reglamento del Sistema de Evaluación de Impacto Ambiental.' },
      { norma: 'Ley 21.455', scope: 'Cambio Climático — metas y planes sectoriales.' },
      { norma: 'DFL N°4/2006', scope: 'Ley General de Servicios Eléctricos.' },
      { norma: 'Ley 19.253', scope: 'Ley Indígena — comunidades y consulta.' },
      { norma: 'LGUC + OGUC', scope: 'Ley General de Urbanismo y Construcciones y su ordenanza — IPT.' },
      { norma: 'Convenio 169 OIT', scope: 'Consulta previa a pueblos indígenas.' },
    ],
    faq: [
      { q: '¿Sirve para preparar una DIA o EIA en Chile?', a: 'Sí. Es uno de los usos principales: cruzar el perímetro con SNASPE, sitios prioritarios MMA, comunidades, glaciares DGA, bosque nativo CONAF, IPT y proyectos SEIA aprobados o en evaluación. Reduce semanas de trabajo de cribado.' },
      { q: '¿Cubre los Instrumentos de Planificación Territorial?', a: 'Sí: PRC con zonas normativas para 16 regiones, PRI/PRMS/PREMVAL/PRMC/PRDU, límites urbanos MINVU (598 polígonos), patrimonio IPT (ICH + ZCH).' },
      { q: '¿Incluye glaciares y derechos de agua?', a: 'Sí. Inventario público de glaciares DGA (IPG 2022, ~26k polígonos) y derechos de aprovechamiento de aguas DAA del SNIA (~76k DAA con actualización diaria).' },
      { q: '¿Hay datos del Sistema Eléctrico Nacional?', a: 'Sí, vía CNE: líneas, subestaciones, centrales del SEN. Útil para evaluar conexión.' },
      { q: '¿Cómo se ven los proyectos SEIA?', a: 'Capa específica con ~30k proyectos DIA y EIA, color por estado (aprobados, en evaluación, rechazados, desistidos).' },
      { q: '¿Cobertura geográfica?', a: 'Atacama → Magallanes. CIREN y MINVU cubren 14-16 regiones según capa. Bosque nativo Maule → Aysén (limitación de la fuente CONAF).' },
    ],
    glossary: [
      { term: 'SEIA', definition: 'Sistema de Evaluación de Impacto Ambiental — Ley 19.300 + DS 40/2012.' },
      { term: 'IPT', definition: 'Instrumento de Planificación Territorial — PRC, PRI, PRMS, PREMVAL, PRMC, PRDU.' },
      { term: 'SNASPE', definition: 'Sistema Nacional de Áreas Silvestres Protegidas del Estado (CONAF).' },
      { term: 'DAA', definition: 'Derecho de Aprovechamiento de Aguas (Código de Aguas — DGA).' },
      { term: 'CITSU', definition: 'Cartas de Inundación por Tsunami — SHOA.' },
      { term: 'ADI', definition: 'Área de Desarrollo Indígena — Ley 19.253 (CONADI).' },
      { term: 'ECMPO', definition: 'Espacio Costero Marino de Pueblos Originarios (Ley Lafkenche).' },
      { term: 'RCA', definition: 'Resolución de Calificación Ambiental — resultado del proceso SEIA.' },
      { term: 'SHAC', definition: 'Sectores Hidrogeológicos de Aprovechamiento Común — DGA.' },
    ],
    legalReference: 'Marco aplicable: Ley 19.300 (Bases Medio Ambiente), D.S. 40/2012 RSEIA, Ley 21.455 (Cambio Climático), DFL 4/2006 (Servicios Eléctricos), Ley 19.253 (Indígena), LGUC + OGUC, Convenio 169 OIT.',
    ctaPrimary: 'Probar gratis',
    ctaSecondary: 'Solicitar demo',
    signupUrl: 'https://aplicacion.geovis.es/auth/signup?source=landing_cl',
  },

  // ─────────────────────────────────────────────────────────────────────────
  pe: {
    code: 'pe',
    hreflang: 'es-PE',
    path: '/pe',
    flag: '🇵🇪',
    countryName: 'Perú',
    countryNative: 'Perú',
    languageLabel: 'Español (Perú)',
    coverageStatus: 'PRO',
    modulesCount: 45,
    title: 'GeoVis Perú · Visor GIS para análisis territorial y certificación ambiental | COFOPRI + SERNANP + SENACE',
    description:
      'Visor GIS para análisis territorial en Perú: catastro urbano COFOPRI, rural MIDAGRI, ANP SERNANP, certificación SENACE, sistema eléctrico SEIN COES, concesiones mineras INGEMMET y +20 capas oficiales. Soporte a SEIA peruano.',
    badge: 'Cobertura PRO · 7 días gratis',
    h1Lead: 'Análisis territorial y',
    h1Highlight: 'certificación ambiental',
    h1Tail: 'en Perú',
    subtitle:
      'Cruza catastro urbano COFOPRI (~67,9k manzanas), catastro rural MIDAGRI (3,18M predios titulados), ANP SERNANP, comunidades campesinas y nativas, concesiones mineras INGEMMET, SEIN COES, patrimonio arqueológico y +20 capas oficiales para due diligence en renovables, minería e hidrocarburos.',
    coverageBlurb:
      'Cobertura nacional para usuarios PRO. 45 módulos integrados: catastro urbano COFOPRI (2,9k centros poblados + 67,9k manzanas), catastro rural MIDAGRI (3,18M predios titulados en 25 departamentos), límites IGN (25 dep · 196 prov · 1.891 dist), Áreas Naturales Protegidas SERNANP, EIAs SENACE, sistema eléctrico SEIN, cartera minera INGEMMET, patrimonio arqueológico MC.',
    sectors: [
      { name: 'Renovables (FV · eólica · geotermia)', body: 'Cruce con ANP, comunidades, capacidad SEIN, recurso solar/eólico y SENACE.' },
      { name: 'Minería y due diligence', body: 'Concesiones INGEMMET, pasivos ambientales, comunidades, glaciares.' },
      { name: 'Hidrocarburos', body: 'Lotes petroleros, ductos, áreas reservadas, comunidades nativas.' },
      { name: 'Agroindustria', body: 'Catastro rural MIDAGRI, plantaciones, cobertura y forestal.' },
      { name: 'Urbanismo y catastro', body: 'COFOPRI catastro urbano nacional y zonificación municipal.' },
      { name: 'Consultoría ambiental', body: 'Cruce previo a EIA-d/EIA-sd con SERNANP, comunidades y patrimonio.' },
    ],
    sourcesHeading: 'Fuentes oficiales integradas',
    sources: [
      { name: 'COFOPRI', desc: 'Catastro urbano nacional — 2,9k centros poblados + 67,9k manzanas.' },
      { name: 'MIDAGRI', desc: 'Catastro rural — 3,18M predios titulados en 25 departamentos.' },
      { name: 'IGN Perú', desc: 'Límites administrativos (25 dep · 196 prov · 1.891 dist), cartografía base.' },
      { name: 'SERNANP', desc: 'Áreas Naturales Protegidas y zonas de amortiguamiento.' },
      { name: 'SENACE', desc: 'EIA-d (Estudios de Impacto Ambiental detallados).' },
      { name: 'MINAM / OEFA', desc: 'Áreas reservadas, ecosistemas, pasivos ambientales.' },
      { name: 'MINEM', desc: 'Proyectos energéticos, concesiones y derechos eléctricos.' },
      { name: 'COES — Comité de Operación del SEIN', desc: 'Sistema interconectado: líneas y subestaciones.' },
      { name: 'INGEMMET', desc: 'Catastro minero, geología, peligros geológicos.' },
      { name: 'Ministerio de Cultura', desc: 'Sitios arqueológicos del patrimonio peruano.' },
    ],
    modules: {
      cadastral: {
        label: 'Administrativo y catastro',
        items: [
          'Límites IGN — 25 departamentos · 196 provincias · 1.891 distritos',
          'Catastro urbano COFOPRI — 2,9k centros poblados + 67,9k manzanas',
          'Catastro rural MIDAGRI — 3,18M predios titulados · 25 dep',
        ],
      },
      urbanismo: {
        label: 'Urbanismo',
        items: [
          'Zonificación urbana municipal',
        ],
      },
      territorial: {
        label: 'Territorial y riesgos',
        items: [
          'Cartera minera INGEMMET',
          'Glaciares peruanos',
          'Geología y geomorfología',
          'Peligros geológicos INGEMMET',
          'Susceptibilidad y sismicidad',
          'Áreas reservadas (proyectos energéticos)',
          'Concesiones mineras y forestales',
          'Pasivos ambientales OEFA',
          'Recurso solar y eólico',
          'Geotermia',
        ],
      },
      infrastructure: {
        label: 'Infraestructura',
        items: [
          'SEIN — Sistema Eléctrico Interconectado Nacional (COES)',
          'Líneas y subestaciones MINEM',
          'Ferrocarriles y puertos',
          'Energía (electricidad, gas, ductos)',
          'Vías nacionales y servicios',
        ],
      },
      environment: {
        label: 'Ambiente y comunidades',
        items: [
          'ANP — Áreas Naturales Protegidas SERNANP + zonas de amortiguamiento',
          'Comunidades campesinas y nativas',
          'Ecosistemas MINAM',
          'Patrimonio arqueológico (Ministerio de Cultura)',
          'Patrimonio cultural',
          'Plantaciones forestales',
          'Frontera y áreas sociales',
        ],
      },
      hydrography: {
        label: 'Hidrografía',
        items: [
          'Red hidrográfica',
          'Cobertura y clima',
        ],
      },
    },
    restrictionsHeading: 'Restricciones detectadas',
    restrictions: [
      { name: 'ANP SERNANP + zonas de amortiguamiento', body: 'Categoría de protección determina la compatibilidad del proyecto.' },
      { name: 'Comunidades campesinas y nativas', body: 'Ley 24656 + Convenio 169 OIT — proceso de consulta previa.' },
      { name: 'Concesiones mineras INGEMMET superpuestas', body: 'Bloqueo o necesidad de acuerdo con titular previo.' },
      { name: 'Patrimonio arqueológico MC', body: 'Sitios catastrados y zonas de potencial — CIRA obligatorio.' },
      { name: 'Servidumbre eléctrica SEIN', body: 'Trazado de líneas de transmisión.' },
      { name: 'Áreas reservadas y zonificación urbana', body: 'Planes de Acondicionamiento Territorial y de Desarrollo Urbano.' },
      { name: 'Pasivos ambientales OEFA', body: 'Riesgo de responsabilidad ambiental en adquisiciones.' },
    ],
    regulatoryFramework: [
      { norma: 'Ley 27446', scope: 'Sistema Nacional de Evaluación del Impacto Ambiental (SEIA).' },
      { norma: 'D.S. 019-2009-MINAM', scope: 'Reglamento de la Ley del SEIA.' },
      { norma: 'Ley 28611', scope: 'Ley General del Ambiente.' },
      { norma: 'Ley 24656', scope: 'Ley General de Comunidades Campesinas.' },
      { norma: 'Ley 29785', scope: 'Derecho a la Consulta Previa (Convenio 169 OIT).' },
      { norma: 'D.L. 1500', scope: 'Promoción de proyectos de inversión.' },
    ],
    faq: [
      { q: '¿Qué incluye en catastro?', a: 'Catastro urbano COFOPRI (2,9k centros poblados + 67,9k manzanas) y catastro rural MIDAGRI (3,18M predios titulados en 25 departamentos). Para una visión completa, se cruzan ambos.' },
      { q: '¿Sirve para una EIA-d con SENACE?', a: 'Sí. Permite cribado inicial sobre ANP, zonas de amortiguamiento, comunidades, patrimonio arqueológico y concesiones — todos elementos críticos para la línea base de un EIA-d.' },
      { q: '¿Trata el tema de comunidades?', a: 'Sí, vía las capas de comunidades campesinas y nativas. No sustituye al proceso de consulta previa formal (Ley 29785), pero permite identificar a tiempo qué comunidades pueden estar afectadas.' },
      { q: '¿Cobertura nacional?', a: 'Sí: los límites IGN cubren 25 departamentos completos. La densidad de capas más profundas (catastro, ANP, minería) es nacional.' },
      { q: '¿Plan requerido?', a: 'La cobertura completa de Perú está incluida en Pro (49 €/mes) y Premium (99 €/mes). El plan Free no incluye Perú.' },
    ],
    glossary: [
      { term: 'ANP', definition: 'Área Natural Protegida — categoría SERNANP.' },
      { term: 'SENACE', definition: 'Servicio Nacional de Certificación Ambiental para Inversiones Sostenibles.' },
      { term: 'CIRA', definition: 'Certificado de Inexistencia de Restos Arqueológicos — Ministerio de Cultura.' },
      { term: 'SEIN', definition: 'Sistema Eléctrico Interconectado Nacional — operado por COES.' },
      { term: 'EIA-d', definition: 'Estudio de Impacto Ambiental detallado.' },
      { term: 'COFOPRI', definition: 'Organismo de Formalización de la Propiedad Informal — catastro urbano.' },
      { term: 'INGEMMET', definition: 'Instituto Geológico, Minero y Metalúrgico — catastro minero.' },
    ],
    legalReference: 'Marco aplicable: Ley 27446 (SEIA), D.S. 019-2009-MINAM, Ley 28611 (LGA), Ley 24656 (Comunidades Campesinas), Ley 29785 (Consulta Previa), D.L. 1500.',
    ctaPrimary: 'Probar gratis',
    ctaSecondary: 'Solicitar demo',
    signupUrl: 'https://aplicacion.geovis.es/auth/signup?source=landing_pe',
  },

  // ─────────────────────────────────────────────────────────────────────────
  it: {
    code: 'it',
    hreflang: 'it-IT',
    path: '/it',
    flag: '🇮🇹',
    countryName: 'Italia',
    countryNative: 'Italia',
    languageLabel: 'Italiano',
    coverageStatus: 'madura',
    modulesCount: 63,
    title: 'GeoVis Italia · Visualizzatore GIS per analisi territoriale e fattibilità progetti | Catasto + Natura 2000 + Aree Idonee FER',
    description:
      'Visualizzatore GIS per analisi territoriale in Italia: catasto regionale, vincoli paesaggistici (D.Lgs 42/2004), aree idonee FER (DM 2024), Rete Natura 2000, ISPRA frane IFFI, sismicità INGV DISS, vincolo idrogeologico, patrimonio MiC. Cobertura 20/20 regioni in numerose categorie.',
    badge: '7 giorni gratis · Senza carta',
    h1Lead: 'Analisi territoriale e',
    h1Highlight: 'fattibilità di progetti',
    h1Tail: 'in Italia',
    subtitle:
      'Visualizzatore GIS che integra catasto regionale (15 regioni), vincoli paesaggistici PPR/PPTR (20/20), aree idonee FER MASE, IFFI ISPRA (~160k frane), sismicità INGV DISS (2.163 sorgenti), vincolo idrogeologico RD 3267/1923, patrimonio MiC ICCD (905.200 beni) e Terna AT/MAT per il cribraggio rapido di progetti rinnovabili e urbanistici.',
    coverageBlurb:
      'Cobertura 20/20 regioni in categorie chiave: 18 dataset paesaggistici locali + SITAP nazionale, 12 dataset urbanistici locali, ISPRA IFFI nazionale, INGV DISS faglie capaci, Terna sistema elettrico, MASE aree idonee FER (DM 2024), MiC patrimonio (905.200 beni · 20/20 regioni). Vincolo idrogeologico in 10 regioni con 7.238 poligoni. Cobertura in espansione attiva.',
    sectors: [
      { name: 'Rinnovabili (FV utility · eolico · eolico offshore)', body: 'Aree idonee FER MASE, capacità Terna, vincolo paesaggistico, sezioni censimento ISTAT.' },
      { name: 'Urbanistica e PPR/PPTR', body: 'Pianificazione paesaggistica regionale (20/20 con 18 dataset locali + SITAP).' },
      { name: 'Due diligence territoriale', body: 'IFFI, sismicità, vincolo idrogeologico, Seveso, SIN MASE.' },
      { name: 'Ingegneria civile', body: 'Reticolo idrografico ISPRA, pericolosità idraulica PGRA, servitù aeronautiche.' },
      { name: 'Ambiente e paesaggio', body: 'Natura 2000, EUAP, IBA LIPU, Carta della Natura, posidonia, costa.' },
      { name: 'Patrimonio culturale', body: 'MiC ICCD — 905.200 beni catalogati su tutte le 20 regioni.' },
    ],
    sourcesHeading: 'Fonti ufficiali integrate',
    sources: [
      { name: 'Agenzia delle Entrate — Catasto', desc: 'Catasto regionale (15 regioni · TOS+LAZ complete · PUG+SIC parziale).' },
      { name: 'ISTAT', desc: 'Confini amministrativi (regioni, province, comuni) e sezioni censimento 2021.' },
      { name: 'ISPRA', desc: 'Corine Land Cover 2018, IFFI (~160k frane), reticolo idrografico, PGRA pericolosità, SIN, Seveso, costa, posidonia, distretti AdBD.' },
      { name: 'MASE', desc: 'Natura 2000 (SIC+ZPS), Aree Idonee FER DM 2024, SIN siti contaminati nazionali, PSEM zone marine.' },
      { name: 'INGV', desc: 'Sismicità DISS — 2.163 sorgenti sismogenetiche, faglie capaci.' },
      { name: 'Terna', desc: 'Sistema elettrico AT/MAT, capacità di connessione.' },
      { name: 'MiC — ICCD', desc: 'Patrimonio culturale — 905.200 beni catalogati nelle 20 regioni.' },
      { name: 'GSE', desc: 'Boschi (132k IT), EUAP (876), aree vulcaniche (40), zone umide Ramsar (66).' },
      { name: 'UNMIG · PITESAI', desc: 'Idrocarburi: 248 concessioni, 1.600 pozzi, 142 cavi sottomarini.' },
      { name: 'LIPU', desc: 'IBA — Important Bird Areas (212 IT).' },
      { name: 'DPC — Protezione Civile', desc: 'Zone allertamento meteo-idro (170 zone) e zone sismiche per comune (7.896).' },
    ],
    modules: {
      cadastral: {
        label: 'Catasto e confini',
        items: [
          'Catasto regionale — 15 regioni (TOS+LAZ complete · PUG+SIC parziale)',
          'Confini ISTAT — regioni, province, comuni',
        ],
      },
      urbanismo: {
        label: 'Pianificazione paesaggistica',
        items: [
          'Paesaggio · PPR/PPTR/PIT-PPR — 20/20 regioni (18 dataset locali + SITAP nazionale per VdA+Molise · D.Lgs 42/2004)',
          'Urbanistica · PGT/PTR/PTRC/PUP/PTCP — 20/20 (12 dataset locali + 8 fallback CLC nazionale · L. 1150/1942)',
        ],
      },
      territorial: {
        label: 'Uso del suolo, FER e dissesto',
        items: [
          'Corine Land Cover ISPRA 2018',
          'DEGURBA Eurostat',
          'Sezioni censimento ISTAT 2021 (distanza minima FER)',
          'Aree Idonee FER MASE — DM 2024',
          'PAI frane · IFFI ISPRA — zone + 160k eventi',
          'Sismicità INGV DISS — 2.163 sorgenti + DPC 7.896 comuni',
          'Consumo di suolo ISPRA per comune 2022',
          'Capacità d\'uso suolo (I-II) — 6 fuentes regionali (89.348 poligoni)',
          'Clima ISPRA SCIA + PNACC — 107 prov',
        ],
      },
      infrastructure: {
        label: 'Infrastrutture',
        items: [
          'Rete stradale nazionale',
          'Rete ferroviaria (AV · convenzionale · metro · tram)',
          'Sistema elettrico Terna AT/MAT',
          'Impianti FV + eolico (94 wind · 79 GW) + UXO (168)',
          'Miniere INSPIRE (3.319) + aggregati marini EMODnet (42)',
          'Idrocarburi UNMIG 248 + pozzi PITESAI (1.600) + cavi (142)',
          'Aerodromi 82 ICAO + servitù LOM/VEN',
        ],
      },
      environment: {
        label: 'Ambiente, paesaggio e patrimonio',
        items: [
          'Rete Natura 2000 (SIC + ZPS) — MASE',
          'IBA LIPU — 212 IT',
          'Carta della Natura + 2.200 tipi paesaggi ISPRA',
          'Vincolo idrogeologico (RD 3267/1923) — 7.238 poligoni in 10 regioni',
          'Vincolo paesaggistico SITAP MiC — art. 136 + 142 + fasce',
          'Boschi (lett. g) GSE — 132k',
          'EUAP aree protette — 876',
          'Zone vulcaniche — 40 · Ghiacciai — 879',
          'Patrimonio culturale MiC ICCD — 905.200 beni (20/20)',
          'Costa ISPRA (63k seg + 12 nm)',
          'PSEM 7 zone marine MASE',
          'Posidonia oceanica EMODnet — 815',
          'Ecosistemi marini EUNIS — 173k habitat',
          'Tratturi (vias pecuarias) — 6 regioni · 1.600',
          'Aree percorse dal fuoco — 8 regioni (54.458 poligoni)',
          'Servitù militari ISPRA — 145',
          'SIN MASE — 13 siti contaminati nazionali',
          'Seveso (RIR) — 978 stabilimenti',
        ],
      },
      hydrography: {
        label: 'Idrografia',
        items: [
          'Bacini · 7 distretti AdBD ISPRA',
          'Reticolo idrografico ISPRA 1:250k (~62k tratti)',
          'Corpi idrici WFD EEA — 8.573 (fiumi+laghi+coste)',
          'Pericolosità idraulica PGRA ISPRA',
          'Zone allertamento meteo-idro DPC — 170',
          'Acquiferi WFD',
          'Zone umide Ramsar GSE — 66',
        ],
      },
    },
    restrictionsHeading: 'Vincoli rilevati',
    restrictions: [
      { name: 'Rete Natura 2000 (SIC + ZPS)', body: 'Bloccante o condizionante per FV utility ed eolico secondo la VINCA.' },
      { name: 'Aree NON idonee per FER (DM 2024)', body: 'Esclude o limita installazioni di rinnovabili in determinati contesti.' },
      { name: 'Vincolo idrogeologico (R.D. 3267/1923)', body: 'Movimenti terra e disboscamento richiedono autorizzazione regionale.' },
      { name: 'Vincolo paesaggistico (D.Lgs. 42/2004)', body: 'Art. 136 (immobili) + 142 (fasce ope legis: 150 m corsi acqua, 300 m coste, ecc.).' },
      { name: 'Servitù aeronautiche militari', body: 'Limitazioni di altezza e zone di protezione.' },
      { name: 'PAI frane (IFFI) e sismicità', body: 'Aree a pericolosità elevata limitano edificabilità.' },
      { name: 'Stabilimenti Seveso (RIR)', body: 'Zone di esclusione attorno a 978 stabilimenti.' },
      { name: 'SIN siti contaminati nazionali', body: '13 SIN con bonifica obbligatoria.' },
      { name: 'Aree archeologiche tutelate', body: 'Patrimonio MiC ICCD — 905.200 beni catalogati.' },
    ],
    regulatoryFramework: [
      { norma: 'D.Lgs. 152/2006', scope: 'Norme in materia ambientale — VIA e VAS.' },
      { norma: 'D.Lgs. 199/2021', scope: 'Promozione delle fonti rinnovabili (FER).' },
      { norma: 'DM Aree Idonee 2024 (MASE)', scope: 'Mappatura nazionale aree idonee/non idonee FER.' },
      { norma: 'D.Lgs. 42/2004', scope: 'Codice dei Beni Culturali e del Paesaggio.' },
      { norma: 'R.D. 3267/1923', scope: 'Vincolo idrogeologico.' },
      { norma: 'L. 1150/1942', scope: 'Legge urbanistica nazionale.' },
      { norma: 'D.Lgs. 105/2015', scope: 'Direttiva Seveso III — rischio di incidenti rilevanti.' },
    ],
    faq: [
      { q: 'Quante regioni sono coperte?', a: 'Cobertura 20/20 regioni in categorie chiave: pianificazione paesaggistica (PPR/PPTR), urbanistica (PGT/PTR/PTRC), patrimonio culturale MiC. Catasto regionale in 15/20 (TOS+LAZ complete, PUG+SIC parziali). Vincolo idrogeologico in 10/20 con 7.238 poligoni.' },
      { q: 'Include le Aree Idonee FER del DM 2024?', a: 'Sì. La capa MASE delle aree idonee FER (DM 2024) è integrata e si incrocia direttamente con il perimetro del progetto fotovoltaico o eolico.' },
      { q: 'Cosa copre il sistema elettrico?', a: 'Terna AT/MAT per il sistema di trasmissione. Impianti FV e eolico (94 wind, 79 GW) per cribraggio competitivo. Eolico offshore in roadmap esteso.' },
      { q: 'Vincolo paesaggistico D.Lgs 42/2004?', a: 'Sì. SITAP nazionale MiC con art. 136 (immobili tutelati) + art. 142 (fasce ope legis: 150 m corsi d\'acqua, 300 m coste, boschi, vulcani, ecc.).' },
      { q: 'Sismicità e frane?', a: 'INGV DISS con 2.163 sorgenti sismogenetiche + DPC zone sismiche per 7.896 comuni. ISPRA IFFI con ~160k eventi catalogati di frane.' },
      { q: 'Patrimonio culturale?', a: 'MiC ICCD — 905.200 beni catalogati in 20/20 regioni. Inclusa la cartografia del vincolo paesaggistico SITAP.' },
    ],
    glossary: [
      { term: 'FER', definition: 'Fonti Energetiche Rinnovabili — D.Lgs. 199/2021.' },
      { term: 'VIA / VAS', definition: 'Valutazione di Impatto Ambientale / Valutazione Ambientale Strategica.' },
      { term: 'VINCA', definition: 'Valutazione di Incidenza Ambientale — per progetti in Natura 2000.' },
      { term: 'SIC / ZPS', definition: 'Siti di Importanza Comunitaria / Zone di Protezione Speciale (Natura 2000).' },
      { term: 'PPR / PPTR / PIT-PPR', definition: 'Piano Paesaggistico Regionale / Territoriale Regionale / d\'Indirizzo Territoriale.' },
      { term: 'PGT / PTR / PTRC / PTCP', definition: 'Strumenti di pianificazione urbanistica regionale e provinciale.' },
      { term: 'SITAP', definition: 'Sistema Informativo Territoriale Ambientale e Paesaggistico — MiC.' },
      { term: 'IFFI', definition: 'Inventario dei Fenomeni Franosi in Italia — ISPRA.' },
      { term: 'DISS', definition: 'Database of Individual Seismogenic Sources — INGV.' },
      { term: 'SIN', definition: 'Siti di Interesse Nazionale per la bonifica — MASE.' },
    ],
    legalReference: 'Quadro applicabile: D.Lgs. 152/2006 (VIA/VAS), D.Lgs. 199/2021 (FER), DM Aree Idonee 2024 MASE, D.Lgs. 42/2004 (Beni Culturali), R.D. 3267/1923 (vincolo idrogeologico), L. 1150/1942, D.Lgs. 105/2015 (Seveso III).',
    ctaPrimary: 'Provalo gratis',
    ctaSecondary: 'Richiedi demo',
    signupUrl: 'https://aplicacion.geovis.es/auth/signup?source=landing_it',
  },

  // ─────────────────────────────────────────────────────────────────────────
  fr: {
    code: 'fr',
    hreflang: 'fr-FR',
    path: '/fr',
    flag: '🇫🇷',
    countryName: 'France',
    countryNative: 'France',
    languageLabel: 'Français',
    coverageStatus: 'opérationnelle',
    modulesCount: 25,
    title: 'GeoVis France · Visualiseur SIG pour l\'analyse territoriale et la faisabilité de projets | Cadastre + GPU + ZAEnR',
    description:
      'Visualiseur SIG pour l\'analyse territoriale en France : cadastre DGFiP, GPU (PLU/SCoT/SUP), ZAEnR loi APER 2023, Natura 2000 INPN, RTE/Enedis, ICPE Géorisques, potentiel éolien et plus. Pour la due diligence, l\'urbanisme et les EnR.',
    badge: '7 jours gratuits · Sans carte',
    h1Lead: 'Analyse territoriale et',
    h1Highlight: 'faisabilité de projets',
    h1Tail: 'en France',
    subtitle:
      'Visualiseur SIG qui intègre cadastre DGFiP, GPU (zonages PLU/PLUi, prescriptions, SUP), Zones d\'Accélération EnR (loi APER 2023), Natura 2000 (ZPS/ZSC), ZNIEFF, RTE/Enedis avec capacités S3REnR, ICPE Géorisques (137k), monuments historiques POP/Mérimée et zones inondables TRI.',
    coverageBlurb:
      'Cobertura nationale avec environ 25 modules de capas officielles : cadastre DGFiP, IGN ADMIN EXPRESS, GPU (Géoportail Urbanisme), INPN (Natura 2000, ZNIEFF, parcs), RTE/Enedis avec capacités d\'accueil S3REnR, BRGM (géologie 1:50k, concessions minières CAMINO), Géorisques (TRI inondations, ICPE 137k, SEVESO), DGAC servitudes aéronautiques.',
    sectors: [
      { name: 'Énergies renouvelables', body: 'Zones d\'Accélération EnR loi APER, capacités S3REnR, potentiel éolien réglementaire (MTE/IGN 2023).' },
      { name: 'Urbanisme (PLU, SCoT)', body: 'Géoportail de l\'Urbanisme : zonages, prescriptions, SUP.' },
      { name: 'Due diligence territoriale', body: 'Cadastre, ICPE, Natura 2000, TRI inondations, monuments historiques.' },
      { name: 'Ingénierie civile', body: 'Géologie BRGM, BD Carthage, masses d\'eau DCE, réseau routier IGN.' },
      { name: 'Environnement', body: 'Natura 2000, ZNIEFF I/II, parcs nationaux, conservatoire du littoral.' },
      { name: 'Agriculture', body: 'RPG parcelles agricoles, OCS GE, CORINE Land Cover.' },
    ],
    sourcesHeading: 'Sources officielles intégrées',
    sources: [
      { name: 'DGFiP — cadastre.gouv.fr', desc: 'Cadastre — parcelles (sous-ensemble départemental).' },
      { name: 'IGN', desc: 'ADMIN EXPRESS, BD TOPO, Route500, OCS GE, RPG, BD Forêt.' },
      { name: 'Géoportail de l\'Urbanisme (GPU)', desc: 'Zonages PLU/PLUi, prescriptions, SUP — données ouvertes.' },
      { name: 'INPN — MNHN', desc: 'Natura 2000 (ZPS/ZSC), ZNIEFF I/II, parcs nationaux/régionaux, sites Ramsar.' },
      { name: 'RTE', desc: 'Réseau de transport HT/THT, capacités d\'accueil S3REnR (Caparéseau).' },
      { name: 'Enedis', desc: 'Réseau de distribution HTA/BT, capacités d\'accueil S3REnR.' },
      { name: 'BRGM', desc: 'Carte géologique 1:50k, concessions minières CAMINO, masses d\'eau souterraines BD LISA.' },
      { name: 'Géorisques', desc: 'ICPE (137k), SEVESO, TRI zones inondables (Directive 2007/60), risques industriels.' },
      { name: 'DGAC', desc: 'Servitudes aéronautiques.' },
      { name: 'SANDRE', desc: 'BD Carthage — cours d\'eau, masses d\'eau DCE.' },
      { name: 'ODRE', desc: 'Open Data Réseaux Énergies — centrales EnR.' },
      { name: 'POP / Mérimée', desc: 'Monuments historiques, base nationale du patrimoine.' },
    ],
    modules: {
      cadastral: {
        label: 'Cadastre et limites administratives',
        items: [
          'Cadastre DGFiP — parcelles',
          'IGN ADMIN EXPRESS — communes, départements, régions',
        ],
      },
      urbanismo: {
        label: 'Urbanisme — Géoportail Urbanisme (GPU)',
        items: [
          'GPU — Zonages PLU/PLUi',
          'GPU — Prescriptions',
          'SUP — Servitudes d\'Utilité Publique',
          'Zones d\'Accélération EnR (loi APER 2023)',
        ],
      },
      territorial: {
        label: 'Territorial — sols, agriculture, géologie',
        items: [
          'RPG — parcelles agricoles (IGN/ASP)',
          'OCS GE — occupation du sol (IGN)',
          'CORINE Land Cover (EEA)',
          'Carte géologique BRGM 1:50k',
          'Concessions minières BRGM CAMINO',
        ],
      },
      infrastructure: {
        label: 'Infrastructure',
        items: [
          'Réseau routier IGN (BD TOPO, Route500)',
          'Réseau ferré SNCF Réseau',
          'Réseau RTE (HT/THT)',
          'Réseau Enedis (HTA/BT)',
          'Centrales EnR (ODRE, IRIS)',
          'Capacités d\'accueil S3REnR (Caparéseau)',
          'Réseau gaz GRTgaz',
          'Servitudes aéronautiques DGAC',
          'Potentiel éolien réglementaire (MTE/IGN 2023, 5 enjeux)',
        ],
      },
      environment: {
        label: 'Environnement et patrimoine',
        items: [
          'Natura 2000 (ZPS/ZSC) — INPN',
          'ZNIEFF I & II — INPN',
          'Parcs nationaux/régionaux',
          'BD Forêt — IGN/ONF',
          'Conservatoire du littoral',
          'Monuments historiques (POP/Mérimée)',
          'Incendies Prométhée BDIFF (15 dépt. méd.)',
          'ICPE · SEVESO (137k)',
          'Émissions GES EU-ETS (Citepa/EEA)',
        ],
      },
      hydrography: {
        label: 'Hydrographie',
        items: [
          'BD Carthage — cours d\'eau (SANDRE)',
          'Masses d\'eau DCE (SANDRE)',
          'TRI — Zones inondables (Directive 2007/60)',
          'Atlas Zones Inondables — DREAL',
          'Masses d\'eau souterraines BD LISA (BRGM)',
          'Zones humides INPN + sites Ramsar',
        ],
      },
    },
    restrictionsHeading: 'Contraintes détectées',
    restrictions: [
      { name: 'Natura 2000 (ZPS / ZSC)', body: 'Évaluation des incidences obligatoire pour projets impactants.' },
      { name: 'ZNIEFF I & II', body: 'Reconnaissance d\'enjeux écologiques — pèse dans l\'instruction.' },
      { name: 'Loi Littoral et Loi Montagne', body: 'Contraintes d\'urbanisme spécifiques.' },
      { name: 'Servitudes aéronautiques DGAC', body: 'Hauteur maximale autorisée près des aéroports.' },
      { name: 'TRI · zones inondables', body: 'Plans de Prévention des Risques d\'inondation.' },
      { name: 'Monuments historiques (abords 500 m)', body: 'Avis ABF requis.' },
      { name: 'ICPE / SEVESO', body: '137k installations classées — zones d\'éloignement.' },
      { name: 'Zones d\'Accélération EnR (loi APER)', body: 'Définissent les zones favorables pour FV / éolien.' },
    ],
    regulatoryFramework: [
      { norma: 'Code de l\'environnement (Livre V)', scope: 'ICPE — Installations Classées pour la Protection de l\'Environnement.' },
      { norma: 'Loi APER (2023)', scope: 'Loi Accélération de la production d\'EnR — Zones d\'Accélération.' },
      { norma: 'Loi Climat et Résilience (2021)', scope: 'Zéro Artificialisation Nette (ZAN), énergie.' },
      { norma: 'Code de l\'urbanisme', scope: 'PLU, PLUi, SCoT — planification locale.' },
      { norma: 'Directive 2007/60/CE', scope: 'Évaluation et gestion des risques d\'inondation (TRI).' },
      { norma: 'Loi Littoral (1986)', scope: 'Urbanisme en zones côtières.' },
      { norma: 'Loi Montagne (1985)', scope: 'Urbanisme en zones de montagne.' },
    ],
    faq: [
      { q: 'Quelle est la couverture cadastrale ?', a: 'Le cadastre DGFiP est intégré sous forme d\'un sous-ensemble départemental. L\'objectif est la cobertura nationale complète sur 2026.' },
      { q: 'Couvrez-vous les Zones d\'Accélération EnR (loi APER) ?', a: 'Oui. Les ZAEnR définies par les communes sont intégrées via GPU et le potentiel éolien réglementaire MTE/IGN 2023 (5 enjeux). Indispensable pour le sourcing de FV utility et éolien.' },
      { q: 'Avez-vous les capacités S3REnR ?', a: 'Oui. Réseau RTE (HT/THT) et Enedis (HTA/BT) avec capacités d\'accueil publiées (Caparéseau). Essentiel pour anticiper la file d\'attente de raccordement.' },
      { q: 'Sources urbanistiques officielles ?', a: 'Géoportail de l\'Urbanisme (GPU) — référentiel national des PLU/PLUi : zonages, prescriptions et SUP.' },
      { q: 'Cadastre rural et agriculture ?', a: 'Oui. RPG — parcelles agricoles (IGN/ASP) + OCS GE (occupation du sol) + CORINE Land Cover. Suffisant pour évaluer l\'éligibilité d\'un site agricole.' },
    ],
    glossary: [
      { term: 'GPU', definition: 'Géoportail de l\'Urbanisme — référentiel national des documents d\'urbanisme.' },
      { term: 'PLU / PLUi', definition: 'Plan Local d\'Urbanisme / intercommunal.' },
      { term: 'SCoT', definition: 'Schéma de Cohérence Territoriale.' },
      { term: 'SUP', definition: 'Servitudes d\'Utilité Publique.' },
      { term: 'ZAEnR', definition: 'Zone d\'Accélération des Énergies Renouvelables — loi APER 2023.' },
      { term: 'ZNIEFF', definition: 'Zone Naturelle d\'Intérêt Écologique, Faunistique et Floristique (type I et II).' },
      { term: 'ICPE', definition: 'Installation Classée pour la Protection de l\'Environnement.' },
      { term: 'TRI', definition: 'Territoires à Risque important d\'Inondation.' },
      { term: 'S3REnR', definition: 'Schéma Régional de Raccordement au Réseau des Énergies Renouvelables.' },
      { term: 'ABF', definition: 'Architecte des Bâtiments de France — avis pour abords monuments historiques.' },
    ],
    legalReference: 'Cadre applicable : Code de l\'environnement (Livre V ICPE), Loi APER 2023, Loi Climat et Résilience 2021, Code de l\'urbanisme (PLU/PLUi/SCoT), Directive 2007/60/CE (TRI), Loi Littoral, Loi Montagne.',
    ctaPrimary: 'Essayer gratuitement',
    ctaSecondary: 'Demander une démo',
    signupUrl: 'https://aplicacion.geovis.es/auth/signup?source=landing_fr',
  },

  // ─────────────────────────────────────────────────────────────────────────
  ro: {
    code: 'ro',
    hreflang: 'ro-RO',
    path: '/ro',
    flag: '🇷🇴',
    countryName: 'România',
    countryNative: 'România',
    languageLabel: 'Română',
    coverageStatus: 'inițială',
    modulesCount: 7,
    earlyAccess: true,
    title: 'GeoVis România · Vizualizator GIS pentru analiza teritorială și fezabilitate | ANCPI + Natura 2000 + RAN',
    description:
      'Vizualizator GIS pentru analiza teritorială în România: limite administrative ANCPI INSPIRE (42 județe · 3.186 UAT), Natura 2000 EEA (383 SCI/SAC + 148 SPA), patrimoniu arheologic RAN INP (28.666 situri), rețea hidrografică ANAR, CORINE Land Cover, transporturi CNAIR + CFR.',
    badge: 'Acces anticipat · În extindere',
    h1Lead: 'Analiza teritorială și',
    h1Highlight: 'fezabilitatea proiectelor',
    h1Tail: 'în România',
    subtitle:
      'Vizualizator GIS care integrează limite administrative ANCPI (42 județe · 3.186 UAT INSPIRE), Natura 2000 EEA (383 SCI/SAC + 148 SPA), patrimoniu RAN (28.666 situri), CORINE 2018, rețea hidrografică ANAR și transporturi CNAIR/CFR. Acoperire în extindere activă cu parteneri locali.',
    coverageBlurb:
      'Acoperire inițială operațională cu 7 capas oficiale active și un roadmap clar pentru 2026: ANCPI cadastru complet, ANRE autorizații, Transelectrica rețea de transport, ANAR resurse de apă extinse, ANPM evaluări de mediu și PUG-uri municipale.',
    sectors: [
      { name: 'Energie regenerabilă', body: 'FV utility, eolian, hibrid + BESS. Cruce cu Natura 2000, transport și RAN.' },
      { name: 'Due diligence teritorială', body: 'Verificare rapidă a constrângerilor înainte de promisiune de cumpărare.' },
      { name: 'Infrastructură', body: 'Drumuri CNAIR, căi ferate CFR, hidrografia ANAR.' },
      { name: 'Patrimoniu și arheologie', body: 'RAN — Repertoriul Arheologic Național (28.666 situri).' },
      { name: 'Agricultură', body: 'CORINE Land Cover 2018 + roadmap ANIF.' },
      { name: 'Consultanță de mediu', body: 'Cruce cu Natura 2000 și extensii ANANP/ANPM în roadmap.' },
    ],
    sourcesHeading: 'Surse oficiale integrate sau în roadmap',
    sources: [
      { name: 'ANCPI', desc: 'Limite administrative INSPIRE — 42 județe + 3.186 UAT. Cadastru complet în roadmap 2026.' },
      { name: 'EEA Natura 2000 RO', desc: '383 SCI/SAC (Directiva Habitate) + 148 SPA (Directiva Păsări).' },
      { name: 'INP / cIMeC', desc: 'RAN — Repertoriul Arheologic Național (28.666 situri).' },
      { name: 'ANAR', desc: 'Rețea hidrografică (5 sub-capas via data.gov.ro), resurse de apă, zone inundabile.' },
      { name: 'EEA CORINE Land Cover', desc: 'Acoperirea solului 2018 — WMS.' },
      { name: 'CNAIR', desc: 'Rețea rutieră națională.' },
      { name: 'CFR', desc: 'Rețea feroviară.' },
      { name: 'ANRE (roadmap)', desc: 'Autorizații și reglementare energie.' },
      { name: 'Transelectrica (roadmap)', desc: 'Rețea de transport și capacitate de conectare.' },
      { name: 'ANPM (roadmap)', desc: 'Evaluări de mediu (EIM/SEA).' },
      { name: 'ANANP (roadmap)', desc: 'Arii naturale protejate naționale.' },
    ],
    modules: {
      cadastral: {
        label: 'Cadastru și limite administrative',
        items: [
          'ANCPI · INSPIRE AU — 42 județe + 3.186 UAT',
          'Cadastru imobiliar (roadmap 2026)',
        ],
      },
      urbanismo: {
        label: 'Urbanism',
        items: [
          'PUG · PUZ municipale (roadmap)',
          'Zone seismice (roadmap)',
        ],
      },
      territorial: {
        label: 'Teritorial — sol, riscuri și regenerabile',
        items: [
          'CORINE Land Cover 2018 (EEA WMS)',
          'Capabilități solare/eoliene (roadmap)',
        ],
      },
      infrastructure: {
        label: 'Infrastructură',
        items: [
          'Transport — drumuri CNAIR + căi ferate CFR (agregat Directiva 2002/49/CE)',
          'Rețea Transelectrica (roadmap)',
          'Aeroporturi și servituți aeronautice (roadmap)',
        ],
      },
      environment: {
        label: 'Mediu și patrimoniu',
        items: [
          'Natura 2000 — 383 SCI/SAC + 148 SPA (EEA)',
          'Patrimoniu arheologic RAN INP/cIMeC — 28.666 situri',
          'Arii naturale protejate ANANP (roadmap)',
          'Monumente istorice LMI (roadmap)',
        ],
      },
      hydrography: {
        label: 'Hidrografie',
        items: [
          'Rețea hidrografică ANAR — 5 sub-capas (data.gov.ro)',
          'Zone inundabile (roadmap)',
        ],
      },
    },
    restrictionsHeading: 'Constrângeri detectate',
    restrictions: [
      { name: 'Arii Natura 2000 (SCI + SPA)', body: 'Cele 531 situri Natura 2000 condiționează sau blochează proiecte impactante.' },
      { name: 'Patrimoniu arheologic RAN', body: 'Cele 28.666 situri arheologice — descărcare de sarcină arheologică obligatorie.' },
      { name: 'Limite UAT', body: 'PUG-urile municipale aplică reguli specifice de uz al solului (roadmap).' },
      { name: 'Zone inundabile (în roadmap)', body: 'Constrângere pentru finanțare și asigurare.' },
      { name: 'Servituți aeronautice (în roadmap)', body: 'Limitări de înălțime în jurul aerodromurilor.' },
      { name: 'Arii protejate ANANP (în roadmap)', body: 'Parcuri naționale și rezervații cu regim strict.' },
    ],
    regulatoryFramework: [
      { norma: 'Legea 292/2018', scope: 'Evaluarea Impactului asupra Mediului (EIM).' },
      { norma: 'OUG 195/2005', scope: 'Protecția mediului — cadru general.' },
      { norma: 'Legea 350/2001', scope: 'Amenajarea teritoriului și urbanismul.' },
      { norma: 'Legea 220/2008', scope: 'Promovarea producerii energiei din surse regenerabile.' },
      { norma: 'HG 1284/2007', scope: 'Declararea SCI și SPA (Natura 2000 RO).' },
      { norma: 'Legea 422/2001', scope: 'Protejarea monumentelor istorice.' },
    ],
    faq: [
      { q: 'Ce este disponibil acum?', a: '7 capas operaționale: limite administrative ANCPI (42 județe + 3.186 UAT), Natura 2000 (531 situri), patrimoniu arheologic RAN (28.666 situri), CORINE Land Cover 2018, rețea hidrografică ANAR, transporturi CNAIR/CFR.' },
      { q: 'Când va fi disponibil cadastrul complet?', a: 'Integrarea cadastrului ANCPI este în roadmap pentru 2026. Înscrieți-vă în accesul anticipat pentru a participa la prioritizarea capas.' },
      { q: 'Acoperiți autorizațiile ANRE?', a: 'În roadmap. Lucrăm cu parteneri locali pentru integrarea ANRE, Transelectrica și ANPM în 2026.' },
      { q: 'Funcționează pentru fezabilitate FV/eolian?', a: 'Pentru cribraj inițial pe baza Natura 2000, RAN și transport, da. Pentru due diligence complet, recomandăm să așteptați extensiile din roadmap sau să cereți o demo de cea mai bună acoperire.' },
      { q: 'Cum participă utilizatorii early access?', a: 'Înscrierea oferă acces la capas operaționale, prioritizarea integrărilor și prețuri preferențiale când lansăm Romania completă.' },
    ],
    glossary: [
      { term: 'ANCPI', definition: 'Agenția Națională de Cadastru și Publicitate Imobiliară.' },
      { term: 'EIM', definition: 'Evaluarea Impactului asupra Mediului — Legea 292/2018.' },
      { term: 'RAN', definition: 'Repertoriul Arheologic Național — INP/cIMeC.' },
      { term: 'PUG / PUZ', definition: 'Plan Urbanistic General / Zonal.' },
      { term: 'SCI / SAC / SPA', definition: 'Situri de Interes Comunitar / Arii Speciale de Conservare / Arii Speciale de Protecție Avifaunistică.' },
      { term: 'ANRE', definition: 'Autoritatea Națională de Reglementare în domeniul Energiei.' },
      { term: 'ANANP', definition: 'Agenția Națională pentru Arii Naturale Protejate.' },
      { term: 'ANPM', definition: 'Agenția Națională pentru Protecția Mediului.' },
    ],
    legalReference: 'Cadru aplicabil: Legea 292/2018 (EIM), OUG 195/2005 (mediu), Legea 350/2001 (urbanism), Legea 220/2008 (regenerabile), HG 1284/2007 (Natura 2000 RO), Legea 422/2001 (patrimoniu).',
    ctaPrimary: 'Înscrie-te pe listă',
    ctaSecondary: 'Vorbește cu echipa',
    signupUrl: 'https://aplicacion.geovis.es/auth/signup?source=landing_ro',
  },

  // ─────────────────────────────────────────────────────────────────────────
  no: {
    code: 'no',
    hreflang: 'nb-NO',
    path: '/no',
    flag: '🇳🇴',
    countryName: 'Norge',
    countryNative: 'Norge',
    languageLabel: 'Norsk',
    coverageStatus: 'completa',
    modulesCount: 47,
    title: 'GeoVis Norge · GIS-visningsverktøy for arealanalyse og prosjektvurdering | Matrikkelen + NVE + Geonorge',
    description:
      'GIS-visningsverktøy for arealanalyse i Norge: Matrikkelen fra Kartverket (3,8 mill. eiendommer), administrative grenser (15 fylker · 357 kommuner), strømnett og fornybar energi fra NVE, skred- og flomrisiko, verneområder (Vern) fra Miljødirektoratet, kulturminner fra Riksantikvaren (862 000 kulturminner) og offshore fra Sokkeldirektoratet. 47 offisielle moduler synkronisert via Geonorge.',
    badge: '7 dager gratis · Uten kort',
    h1Lead: 'Arealanalyse og',
    h1Highlight: 'prosjektvurdering',
    h1Tail: 'i Norge',
    subtitle:
      'GIS-visningsverktøy som samler Matrikkelen fra Kartverket, administrative grenser, strømnett og nettkapasitet fra NVE, vannkraft, vindkraft, solenergi og offshore olje/gass + CO₂-lagring fra Sokkeldirektoratet, skred- og kvikkleirerisiko, flomsoner, verneområder (Vern) og kulturminner fra Riksantikvaren. 47 offisielle datalag synkronisert via Geonorge.',
    coverageBlurb:
      'Full nasjonal dekning over de 385 207 km² som utgjør Fastlands-Norge og den økonomiske sonen: 47 offisielle datalag fordelt på 9 grupper (Matrikkel, Arealplanlegging, Grunnforhold, Infrastruktur, Energi, Miljø, Hydrografi, Kulturminner, Marint og Fiskeri). Visningsverktøyet samler åpne data fra Kartverket, NVE, NIBIO, Miljødirektoratet, Sokkeldirektoratet, Riksantikvaren, Fiskeridirektoratet og SSB, publisert gjennom Geonorge under lisensen Norge digitalt, med full sporbarhet per lag og verktøy for kryssjekk, søk og profesjonell eksport. Svalbard og Jan Mayen omfattes ikke av fastlandsdekningen.',
    sectors: [
      { name: 'Fornybar energi (vannkraft · vindkraft · solenergi)', body: 'Vannkraft, vindkraft og NVEs vindressurskart, kryssjekket med strømnett og nettkapasitet.' },
      { name: 'Datasentre (lokalisering)', body: 'Rimelig vannkraft, kaldt klima og nettkapasitet via WattApp for valg av lokalisering.' },
      { name: 'Offshore olje/gass + CO₂-lagring', body: 'Utvinningstillatelser, brønner, infrastruktur og CO₂-lisenser (Northern Lights) fra Sokkeldirektoratet.' },
      { name: 'Arealmessig due diligence', body: 'Kartlegging av kritiske bindinger før inngåelse av opsjonsavtaler på tomt: Vern, skred, flom, reindrift.' },
      { name: 'Anleggsteknikk og geoteknikk', body: 'Grunnundersøkelser fra NADAG, kvikkleire, NGUs geologiske data og flomsoner.' },
      { name: 'Miljørådgivning', body: 'Kryssjekk mot verneområder (Vern), NiN-naturtyper, Ramsar-områder og forurenset grunn fra Miljødirektoratet.' },
      { name: 'Infrastruktur og samferdsel', body: 'Veinett fra NVDB, jernbane fra Bane NOR, maritim infrastruktur fra Kystverket og luftfart fra Avinor.' },
      { name: 'Jordbruk og skogbruk', body: 'Arealressurskart AR5, jordbruksareal og jordsmonnkvalitet fra NIBIO.' },
    ],
    sourcesHeading: 'Integrerte offisielle datakilder',
    sources: [
      { name: 'Kartverket / Matrikkelen', desc: 'Nasjonalt eiendomsregister (3,8 mill. eiendommer, gnr/bnr), administrative grenser (15 fylker · 357 kommuner) og maritime grenser.' },
      { name: 'Geonorge', desc: 'Nasjonal geoportal driftet av Kartverket, som samler åpne data under lisensen Norge digitalt (Geodataloven 2010, INSPIRE/EØS).' },
      { name: 'NVE', desc: 'Strømnett (TSO/DSO), fornybar energi, hydrografi, risiko (skred, flom, kvikkleire) og hydrologiske målestasjoner.' },
      { name: 'Sokkeldirektoratet (Sodir)', desc: 'Offshore: brønner, installasjoner, utvinningstillatelser, CO₂-lisenser og mineraler på havbunnen.' },
      { name: 'Miljødirektoratet / Naturbase', desc: 'Verneområder (Vern), Ramsar-områder, NiN-naturtyper og forurenset grunn (Grunnforurensning).' },
      { name: 'Riksantikvaren', desc: 'Kulturarv: kulturminner, fredede monumenter og bygninger (862 000 registreringer).' },
      { name: 'NIBIO', desc: 'Arealressurskart AR5, jordbruksareal, jordsmonnkvalitet og samisk reindrift.' },
      { name: 'NGU', desc: 'Geologi (berggrunn/løsmasser), grunnundersøkelser fra NADAG og grunn geotermi (GRANADA).' },
      { name: 'Fiskeridirektoratet', desc: 'Akvakultur, fiskeriområder, marine forbudssoner og beskyttede bunnhabitater.' },
      { name: 'SSB', desc: 'Tettsteder, grunnkretser og arealreserver.' },
    ],
    modules: {
      cadastral: {
        label: 'Matrikkel og administrativ inndeling',
        items: [
          'Matrikkelen — 3,8 mill. eiendommer (gnr/bnr + areal) · Kartverket',
          'Administrative grenser — 15 fylker + 357 kommuner · Kartverket',
          'Grunnkretser og tettsteder · SSB',
          'Jordbruksareal og jordsmonnkvalitet (Jordbruksareal · Jordsmonn · NIBIO)',
        ],
      },
      urbanismo: {
        label: 'Arealplanlegging',
        items: [
          'Reguleringsplaner under behandling (DiBK · 357 kommuner samlet)',
          'Arealreserver for bolig og industri (Arealreserver · SSB)',
        ],
      },
      territorial: {
        label: 'Grunnforhold og geologi',
        items: [
          'Arealressurskart AR5 (NIBIO · 634 000 polygoner)',
          'NGU-geologi — berggrunn og løsmasser',
          'Grunnundersøkelser NADAG (616 000 boringer)',
          'Offshore-geologi og blokkinndeling (Sokkeldirektoratet)',
        ],
      },
      infrastructure: {
        label: 'Infrastruktur og energi',
        items: [
          'Veier NVDB, jernbane Bane NOR, maritimt Kystverket, luftfart Avinor',
          'Strømnett NVE (sentralnett + regionalnett + distribusjonsnett + sjøkabler)',
          'Nettkapasitet (WattApp · 824 transformatorstasjoner)',
          'Vannkraft, vindkraft, solceller, grunn geotermi og fjernvarme (NVE)',
          'Offshore olje/gass + CO₂-lagring — utvinningstillatelser og CO₂-lisenser (Sokkeldirektoratet)',
          'Militære områder (Forsvar SOF) og luftfartsrestriksjoner',
        ],
      },
      environment: {
        label: 'Miljø og kulturminner',
        items: [
          'Verneområder (Vern) og Ramsar-våtmarker · Miljødirektoratet',
          'NiN-naturtyper og prioriterte naturtyper (Naturmangfoldloven §52)',
          'Samisk reindrift (Reindrift · NIBIO/Sametinget)',
          'Forurenset grunn (Grunnforurensning · 15 000 lokaliteter)',
          'Kulturminner Riksantikvaren — 862 000 kulturminner',
          'Akvakultur, fiskeri og beskyttede marine habitater (Fiskeridirektoratet)',
        ],
      },
      hydrography: {
        label: 'Hydrografi, vann og risiko',
        items: [
          'Hydrografi NVE — elver, innsjøer, breer og vernede vassdrag (Verneplan)',
          'Flomsoner Q10/Q100/Q500/Q1000 (Flomsoner · lovpålagt terskel TEK17 §7)',
          'Klimafremskrevne flomscenarioer (Flomsoner Klima)',
          'Skred og kvikkleire (Skred · kvikkleire) og marin leire (MarinGrense)',
          'Sikringstiltak NVE og hydrologiske målestasjoner',
        ],
      },
    },
    restrictionsHeading: 'Blokkerende restriksjoner oppdaget',
    restrictions: [
      { name: 'Verneområder (Vern)', body: 'Nasjonalparker, naturreservater og vernede landskap kan begrense eller stoppe prosjekter i henhold til Naturmangfoldloven.' },
      { name: 'Kvikkleire', body: 'Kvikkleiresoner krever obligatoriske geotekniske undersøkelser (lærdommen fra Gjerdrum 2020).' },
      { name: 'Flomsoner', body: 'Lovpålagt terskel i TEK17 §7 over Q200; påvirker forsikring, finansiering og byggetillatelse.' },
      { name: 'Samisk reindrift (Reindrift)', body: 'Beskyttet rettighet (Reindriftsloven) med juridisk bindende områder og flyttleier i Nord-Norge.' },
      { name: 'Forurenset grunn (Grunnforurensning)', body: 'Tilstandsklasse 1-5 som utløser krav om opprydding etter Forurensningsloven.' },
      { name: 'Kulturminner', body: 'Automatisk fredede kulturminner (Kulturminneloven) med sikringssoner.' },
      { name: 'Militære områder (Forsvar)', body: 'Skyte- og øvingsfelt med bruksbegrensninger og støysoner.' },
      { name: 'Nettkapasitet', body: 'Uten ledig MW-kapasitet publisert for nærmeste transformatorstasjon får produksjonsprosjektet ikke nettilknytning.' },
    ],
    regulatoryFramework: [
      { norma: 'Plan- og bygningsloven', scope: 'Plan- og bygningsloven — kommunale planer og byggetillatelser.' },
      { norma: 'Naturmangfoldloven', scope: 'Lov om naturmangfold — verneområder og prioriterte naturtyper (§52).' },
      { norma: 'TEK17 §7', scope: 'Byggteknisk forskrift — sikkerhetskrav mot flom og skred.' },
      { norma: 'Kulturminneloven', scope: 'Kulturminneloven — automatisk fredning av kulturminner.' },
      { norma: 'Energiloven', scope: 'Energiloven — nettkonsesjoner og tillatelse til kraftproduksjon.' },
      { norma: 'Reindriftsloven', scope: 'Reindriftsloven — rettigheter og vernede områder for samisk reindrift.' },
      { norma: 'Forurensningsloven', scope: 'Forurensningsloven — forurenset grunn og opprydding.' },
      { norma: 'Geodataloven', scope: 'Geodataloven fra 2010 — gjennomføring av INSPIRE via EØS-avtalen.' },
    ],
    faq: [
      { q: 'Erstatter GeoVis QGIS eller ArcGIS?', a: 'Nei. GeoVis er et visningsverktøy for kartlegging og due diligence, med de offisielle norske datalagene ferdig innlastet og synkronisert via Geonorge. QGIS og ArcGIS er fortsatt nødvendig for avansert geoprosessering, tunge rasterdata eller skreddersydd ETL. De fleste team bruker GeoVis til rask kartlegging og QGIS kun til den siste detaljerte analysen.' },
      { q: 'Hvor kommer dataene fra?', a: 'Fra offisielle norske kilder: Kartverket (Matrikkelen, grenser), NVE (nett, fornybar energi, risiko, hydrografi), NIBIO, Miljødirektoratet, Sokkeldirektoratet, Riksantikvaren, NGU, Fiskeridirektoratet og SSB. Nesten alt samles gjennom Geonorge under lisensen Norge digitalt (Geodataloven 2010, INSPIRE via EØS-avtalen).' },
      { q: 'Dekker det hele matrikkelen?', a: 'Ja. Visningsverktøyet integrerer alle 3,8 millioner eiendommer (gnr/bnr og areal) fra Matrikkelen for hele Fastlands-Norge, i tillegg til grensene for alle 15 fylker og 357 kommuner.' },
      { q: 'Egner det seg for vurdering av fornybarprosjekter og datasentre?', a: 'Ja, det er et av hovedbruksområdene: vannkraft, vindkraft, solenergi og NVEs vindressurskart kryssjekket med strømnettet og nettkapasiteten som publiseres per transformatorstasjon (WattApp). Norge er et populært mål for datasentre på grunn av rimelig vannkraft og kaldt klima.' },
      { q: 'Inkluderer det Svalbard og Jan Mayen?', a: 'Nei. Dekningen omfatter Fastlands-Norge og den økonomiske sonen. Svalbard og Jan Mayen er ikke inkludert; disse dataene publiseres separat av Norsk Polarinstitutt.' },
      { q: 'Kan jeg eksportere det jeg ser på kartet?', a: 'Ja, i Pro- og Premium-planen: KMZ, GeoJSON, Shapefile og PDF. Pay-per-use-rapporten kryssjekker de offisielle datalagene med det angitte området og gjeldende regelverk.' },
    ],
    glossary: [
      { term: 'Matrikkelen', definition: 'Norges nasjonale eiendomsregister (gnr/bnr) forvaltet av Kartverket.' },
      { term: 'Geonorge', definition: 'Norges nasjonale geoportal, driftet av Kartverket; INSPIRE-node under Geodataloven.' },
      { term: 'NVE', definition: 'Norges vassdrags- og energidirektorat.' },
      { term: 'Fylke / Kommune', definition: 'Administrativt nivå 1 (15 fylker) og nivå 2 (357 kommuner).' },
      { term: 'Skred / kvikkleire', definition: 'Skred og kvikkleire — geoteknisk risiko under TEK17.' },
      { term: 'Flomsoner', definition: 'NVEs flomsoner etter gjentaksintervall (Q10/Q100/Q500/Q1000).' },
      { term: 'Vern', definition: 'Vernede naturområder under Naturmangfoldloven (Miljødirektoratet).' },
      { term: 'Reindrift', definition: 'Samisk reindrift — beskyttet rettighet med bindende områder og flyttleier.' },
      { term: 'Sodir', definition: 'Sokkeldirektoratet — direktoratet for kontinentalsokkelen (offshore olje/gass og CO₂-lagring).' },
    ],
    legalReference: 'Gjeldende regelverk: Plan- og bygningsloven (arealplanlegging og bygging), Naturmangfoldloven (naturmangfold), TEK17 §7 (byggteknisk forskrift), Kulturminneloven (kulturminner), Energiloven (energi), Reindriftsloven (reindrift), Forurensningsloven (forurensning) og Geodataloven (geodata · INSPIRE via EØS-avtalen).',
    ctaPrimary: 'Start gratis',
    ctaSecondary: 'Be om demo',
    signupUrl: 'https://aplicacion.geovis.es/auth/signup?source=landing_no',
  },

  // ─────────────────────────────────────────────────────────────────────────
  pl: {
    code: 'pl',
    hreflang: 'pl-PL',
    path: '/pl',
    flag: '🇵🇱',
    countryName: 'Polska',
    countryNative: 'Polska',
    languageLabel: 'Polski',
    coverageStatus: 'kompletna',
    modulesCount: 49,
    title: 'GeoVis Polska · Przeglądarka GIS do analizy terenowej i wykonalności projektów | GUGiK + Natura 2000 GDOŚ + LPIS ARiMR',
    description:
      'Przeglądarka GIS do analizy terenowej i wyboru lokalizacji inwestycji w Polsce: granice administracyjne GUGiK PRG (16 województw · 380 powiatów · 2479 gmin), Natura 2000 GDOŚ (145 OSO + 871 SOO), działki rolne LPIS ARiMR, sieć elektroenergetyczna PSE 220/400/750 kV, górnictwo MIDAS PIG-PIB, zagrożenie powodziowe ISOK Q10/Q100/Q500, planowanie miejscowe MPZP i ponad 40 oficjalnych warstw danych. Raport PDF w mniej niż 24 godziny.',
    badge: '7 dni za darmo · Bez karty',
    h1Lead: 'Analiza terenowa i',
    h1Highlight: 'wykonalność projektów',
    h1Tail: 'w Polsce',
    subtitle:
      'Przeglądarka GIS z granicami administracyjnymi GUGiK PRG, działkami rolnymi LPIS ARiMR, obszarami Natura 2000 i terenami chronionymi GDOŚ, siecią elektroenergetyczną PSE, górnictwem MIDAS, zagrożeniem powodziowym ISOK, planowaniem MPZP, wybrzeżem bałtyckim SIPAM i ponad 40 oficjalnymi warstwami danych. Dla OZE, due diligence terenowego, logistyki przemysłowej, ochrony środowiska i górnictwa.',
    coverageBlurb:
      'Zasięg krajowy oparty na najdojrzalszej infrastrukturze INSPIRE w Unii Europejskiej. GUGiK zarządza węzłem krajowym (geoportal.gov.pl) z usługą WFS granic PRG oraz WMS ortofotomapy, NMT LiDAR, EGiB i MPZP. GDOŚ udostępnia najbardziej kompletną usługę WFS przyrody (23 warstwy: Natura 2000, parki narodowe i krajobrazowe, rezerwaty, korytarze ekologiczne, Pomniki Przyrody, Ramsar). ARiMR udostępniła LPIS w grudniu 2025 r. (ok. 14 mln działek rolnych). Przeglądarka integruje ponadto dane PSE, GDDKiA, PKP PLK, MIDAS PIG-PIB, BDL Lasy Państwowe, NID, SIPAM, ULC, ISOK i Instrat OZE wraz z narzędziami do nakładania warstw, próbkowania rastrów gleby, nasłonecznienia i wiatru oraz profesjonalnego eksportu danych.',
    sectors: [
      { name: 'OZE (farmy fotowoltaiczne · elektrownie wiatrowe onshore/offshore)', body: 'Nakładanie z obszarami Natura 2000 GDOŚ, dostępną mocą przyłączeniową PSE, powierzchniami ograniczającymi przeszkody lotnicze ULC oraz wnioskami offshore SIPAM na Bałtyku.' },
      { name: 'Due diligence terenowe', body: 'Szybka weryfikacja portfeli działek i identyfikacja krytycznych ograniczeń przed zawarciem umowy przedwstępnej.' },
      { name: 'Logistyka i lokalizacje przemysłowe', body: 'Korytarz CPK Łódź–Warszawa, drogi GDDKiA, kolej PKP PLK oraz specjalne strefy ekonomiczne SSE.' },
      { name: 'Sektor górniczy i wydobywczy', body: 'MIDAS PIG-PIB ze złożami, obszarami i terenami górniczymi (węgiel, miedź KGHM, JSW).' },
      { name: 'Doradztwo środowiskowe', body: 'Natura 2000, korytarze ekologiczne, tereny chronione GDOŚ oraz zakłady E-PRTR/SEVESO na potrzeby ocen oddziaływania na środowisko.' },
      { name: 'Rolnictwo', body: 'Działki referencyjne LPIS ARiMR, CORINE Land Cover oraz właściwości gleby SoilGrids.' },
      { name: 'Lokalizacje centrów danych', body: 'Moc przyłączeniowa PSE, NMT LiDAR do analizy posadowienia oraz jakość powietrza GIOŚ w cyfrowym węźle Europy Środkowej.' },
      { name: 'Inżynieria lądowa', body: 'Trasowanie względem hydrografii, zagrożenia powodziowego ISOK, rzeźby terenu NMT oraz stref ograniczeń lotniczych OLS.' },
    ],
    sourcesHeading: 'Zintegrowane źródła oficjalne',
    sources: [
      { name: 'GUGiK · PRG', desc: 'Granice administracyjne INSPIRE — 16 województw + 380 powiatów + 2479 gmin (TERYT GUS).' },
      { name: 'GDOŚ', desc: 'Natura 2000 (145 OSO + 871 SOO), parki narodowe i krajobrazowe, rezerwaty, korytarze ekologiczne i obszary Ramsar (WFS, 23 warstwy).' },
      { name: 'ARiMR · LPIS', desc: 'Działki referencyjne rolne (ok. 14 mln) udostępnione przez WMS GeoServer, otwarte w grudniu 2025 r.' },
      { name: 'GUGiK · MPZP / EGiB', desc: 'Krajowa Integracja: plany miejscowe (ok. 32% powierzchni kraju) oraz agregator katastralny EGiB.' },
      { name: 'PSE', desc: 'Sieć przesyłowa TSO — linie 220/400/750 kV, stacje elektroenergetyczne oraz połączenia transgraniczne.' },
      { name: 'PIG-PIB · MIDAS', desc: 'Górnictwo — 18 066 złóż + 6273 obszarów górniczych + 6093 terenów górniczych.' },
      { name: 'KZGW / Wody Polskie · ISOK', desc: 'Mapy zagrożenia powodziowego Q10/Q100/Q500 (dyrektywa 2007/60/WE, za pośrednictwem JRC EFAS).' },
      { name: 'GDDKiA + PKP PLK', desc: 'Sieć dróg krajowych (autostrady, drogi ekspresowe) oraz sieć kolejowa.' },
      { name: 'NID', desc: 'Narodowy Instytut Dziedzictwa — zabytki udostępniane przez oficjalny tileserver.' },
      { name: 'SIPAM (Urząd Morski)', desc: 'Wybrzeże bałtyckie: 40 portów, 50 przystani, infrastruktura hydrotechniczna oraz wnioski dotyczące morskiej energetyki wiatrowej.' },
      { name: 'ULC', desc: 'Certyfikowane lotniska oraz powierzchnie ograniczające przeszkody lotnicze (OLS).' },
      { name: 'BDL Lasy Państwowe', desc: 'Nadleśnictwa Lasów Państwowych — 429 jednostek.' },
      { name: 'GIOŚ + EEA', desc: 'Jakość powietrza (288 stacji), CORINE Land Cover 2018, E-PRTR, SEVESO oraz UWWTD.' },
      { name: 'Instrat OZE', desc: 'Rejestr instalacji OZE (9959 elektrowni · 37 GW), otwarty zamiennik rejestru URE.' },
    ],
    modules: {
      cadastral: {
        label: 'Katastr i podział administracyjny',
        items: [
          'Granice GUGiK PRG — 16 województw + 380 powiatów + 2479 gmin (TERYT)',
          'Działki referencyjne LPIS ARiMR — ok. 14 mln działek rolnych (WMS)',
          'Spis NSP 2021 — siatka gęstości zaludnienia 1 km (201 520 komórek)',
          'EGiB — krajowy agregator katastralny (WMS, podzielony na powiaty)',
        ],
      },
      urbanismo: {
        label: 'Urbanistyka i planowanie',
        items: [
          'MPZP — Krajowa Integracja planów miejscowych (ok. 32% powierzchni kraju)',
          'Katastr górniczy MIDAS — koncesje oraz obszary górnicze',
          'Dziedzictwo kulturowe NID (zabytki)',
          'Specjalne strefy ekonomiczne SSE',
        ],
      },
      territorial: {
        label: 'Grunty, zagrożenia i energia odnawialna',
        items: [
          'CORINE Land Cover 2018 — pokrycie terenu (ok. 195 tys. poligonów)',
          'SoilGrids 250 m — pH, węgiel organiczny i zawartość iłu w wierzchniej warstwie gleby (próbkowanie hover/click)',
          'Górnictwo MIDAS PIG-PIB — złoża, obszary i tereny górnicze',
          'Nasłonecznienie GHI i wiatr na 100 m (Global Solar/Wind Atlas, raster)',
          'OZE Instrat — 9959 instalacji (37 GW)',
          'Rzeźba terenu NMT LiDAR GRID 1 m + dane leśne BDL Lasy Państwowe (429 nadleśnictw)',
        ],
      },
      infrastructure: {
        label: 'Infrastruktura',
        items: [
          'Sieć elektroenergetyczna PSE — linie 220/400/750 kV + stacje elektroenergetyczne + połączenia transgraniczne',
          'Drogi krajowe GDDKiA (autostrady A · drogi ekspresowe S · DK)',
          'Kolej PKP PLK — linie + stacje',
          'Gazociągi GAZ-SYSTEM + ropociągi PERN',
          'CPK — Obszar otoczenia nowego hubu lotniczego (18 gmin · 1080 km²)',
          'Wybrzeże bałtyckie SIPAM — porty, przystanie i infrastruktura hydrotechniczna',
          'Lotniska ULC oraz powierzchnie ograniczające przeszkody lotnicze (OLS)',
        ],
      },
      environment: {
        label: 'Środowisko i dziedzictwo',
        items: [
          'Natura 2000 GDOŚ — 145 OSO (ptasie) + 871 SOO (siedliskowe)',
          'Tereny chronione GDOŚ — 46 parków narodowych, 213 parków krajobrazowych, 2070 rezerwatów, 19 obszarów Ramsar',
          'Korytarze ekologiczne, Obszary Chronionego Krajobrazu i Pomniki Przyrody',
          'Dziedzictwo kulturowe NID (zabytki) przez oficjalny tileserver',
          'Zakłady E-PRTR (4576) oraz SEVESO III (189 zakładów)',
          'Jakość powietrza GIOŚ — 288 stacji PMŚ',
        ],
      },
      hydrography: {
        label: 'Hydrografia i wody',
        items: [
          'Sieć hydrograficzna — rzeki (HydroRIVERS) i główne jeziora',
          'Zagrożenie powodziowe ISOK Q10 / Q100 / Q500 (dyrektywa 2007/60/WE, za pośrednictwem JRC EFAS)',
          'Oczyszczalnie ścieków UWWTD — 1642 obiekty (dyrektywa 91/271/EWG)',
          'Wody kąpieliskowe — 764 oficjalne kąpieliska (dyrektywa 2006/7/WE)',
        ],
      },
    },
    restrictionsHeading: 'Wykryte ograniczenia blokujące',
    restrictions: [
      { name: 'Natura 2000 (OSO + SOO)', body: 'Ponad 1000 obszarów GDOŚ warunkuje lub blokuje farmy fotowoltaiczne i elektrownie wiatrowe w zależności od planu zarządzania.' },
      { name: 'Tereny chronione GDOŚ', body: 'Parki narodowe, parki krajobrazowe, rezerwaty i korytarze ekologiczne o odrębnych reżimach ochronnych.' },
      { name: 'Zagrożenie powodziowe ISOK (Q10/Q100/Q500)', body: 'Klasy głębokości zalania wpływają na finansowanie bankowe i ubezpieczenie projektu.' },
      { name: 'Powierzchnie OLS ograniczające przeszkody lotnicze (ULC)', body: 'Ograniczają dopuszczalną wysokość konstrukcji fotowoltaicznych, turbin wiatrowych i masztów w pobliżu lotnisk.' },
      { name: 'CPK · Obszar otoczenia', body: 'Poligon regulacyjny obejmujący 18 gmin (1080 km²) wokół nowego hubu lotniczego, warunkujący wybór lokalizacji inwestycji.' },
      { name: 'Obszary i tereny górnicze MIDAS', body: 'Koncesje (obszary/tereny górnicze) ograniczają sposób użytkowania powierzchni terenu.' },
      { name: 'Dziedzictwo kulturowe NID', body: 'Obiekty wpisane do rejestru (zabytki) wymagają nadzoru archeologicznego i ochrony otoczenia.' },
      { name: 'Moc przyłączeniowa PSE', body: 'Bez dostępnej mocy przyłączeniowej w najbliższej stacji elektroenergetycznej projekt nie może zostać przyłączony do sieci przesyłowej.' },
    ],
    regulatoryFramework: [
      { norma: 'Ustawa 16.04.2004 (Ochrona przyrody)', scope: 'Ochrona przyrody — Natura 2000 i tereny chronione GDOŚ.' },
      { norma: 'Ustawa 27.03.2003 (Planowanie przestrzenne)', scope: 'Planowanie i zagospodarowanie przestrzenne — MPZP/SUiKZP (reforma z 2023 r.).' },
      { norma: 'Ustawa 3.10.2008 (Ocena oddziaływania na środowisko)', scope: 'Ocena oddziaływania na środowisko (OOŚ) oraz dostęp do informacji o środowisku.' },
      { norma: 'Prawo energetyczne (10.04.1997)', scope: 'Sektor elektroenergetyczny — zezwolenia oraz dostęp do sieci przesyłowej PSE.' },
      { norma: 'Prawo wodne (20.07.2017)', scope: 'Gospodarka wodna — wody publiczne oraz zagrożenie powodziowe (dyrektywa 2007/60/WE).' },
      { norma: 'Prawo geologiczne i górnicze (9.06.2011)', scope: 'Geologia i górnictwo — koncesje, złoża oraz obszary górnicze MIDAS.' },
    ],
    faq: [
      { q: 'Czy GeoVis zastępuje QGIS lub ArcGIS?', a: 'Nie. GeoVis to przeglądarka do wstępnej weryfikacji i due diligence z gotowymi, zsynchronizowanymi oficjalnymi polskimi warstwami danych. QGIS i ArcGIS są nadal potrzebne do zaawansowanego geoprzetwarzania, ciężkich rastrów czy niestandardowego ETL. Większość zespołów korzysta z GeoVis do szybkiej wstępnej analizy i raportu, a z QGIS tylko do finalnych szczegółów.' },
      { q: 'Z jakich oficjalnych źródeł pochodzą warstwy?', a: 'Z infrastruktury INSPIRE GUGiK (granice PRG, EGiB, MPZP, ortofotomapa, NMT LiDAR), z usługi WFS przyrody GDOŚ (23 warstwy), z ARiMR (LPIS), PSE (sieć elektroenergetyczna), PIG-PIB (górnictwo MIDAS), GDDKiA i PKP PLK (transport), NID (dziedzictwo), SIPAM (wybrzeże bałtyckie), ULC (lotnictwo), GIOŚ i EEA (jakość powietrza, CORINE, przemysł). Każda warstwa pokazuje datę ostatniej aktualizacji.' },
      { q: 'Czy dostępny jest katastr działek (EGiB)?', a: 'Katastr EGiB jest udostępniany jako krajowy agregator WMS GUGiK. Nie istnieje krajowy zbiór wektorowy bulk, ponieważ dane są rozproszone pomiędzy 380 starostw powiatowych. Do analiz rolniczych wykorzystujemy zamiast tego ok. 14 mln działek referencyjnych LPIS z ARiMR, udostępnionych w grudniu 2025 r.' },
      { q: 'Czy nadaje się do oceny wykonalności farm fotowoltaicznych i elektrowni wiatrowych onshore/offshore?', a: 'Tak. To jedno z głównych zastosowań: nakładanie z obszarami Natura 2000 GDOŚ, dostępna moc przyłączeniowa PSE, powierzchnie OLS dopuszczalnej wysokości (ULC), zagrożenie powodziowe ISOK oraz, w przypadku offshore, wnioski dotyczące morskich farm wiatrowych na Bałtyku za pośrednictwem SIPAM.' },
      { q: 'Czy mogę wyeksportować to, co widzę na mapie?', a: 'Tak, w planach Pro i Premium: KMZ, GeoJSON, Shapefile oraz PDF. Raport PDF w modelu pay-per-use zawiera warstwy nałożone na obrys działki wraz z obowiązującymi polskimi ramami prawnymi.' },
      { q: 'Jak liczona jest cena raportu pay-per-use?', a: '99 € opłaty bazowej (do 10 ha) + 2 €/ha za każdy dodatkowy hektar (Premium: 1 €/ha). Limit 999 €. Plan Premium obejmuje 1 darmowy raport miesięcznie do 100 ha. Realizacja w mniej niż 24 godziny.' },
    ],
    glossary: [
      { term: 'GUGiK', definition: 'Główny Urząd Geodezji i Kartografii — krajowy urząd kartograficzny zarządzający geoportalem INSPIRE.' },
      { term: 'GDOŚ', definition: 'Generalna Dyrekcja Ochrony Środowiska — organ ochrony środowiska (Natura 2000, tereny chronione).' },
      { term: 'EGiB', definition: 'Ewidencja Gruntów i Budynków — katastr gruntów i budynków (krajowy agregator WMS).' },
      { term: 'LPIS', definition: 'Działki referencyjne rolne ARiMR — podstawa systemu dopłat WPR w Polsce.' },
      { term: 'MPZP', definition: 'Dokument planistyczny gminy określający przeznaczenie i zasady zagospodarowania terenu.' },
      { term: 'OSO / SOO', definition: 'Obszar Specjalnej Ochrony (ptaki) / Specjalny Obszar Ochrony (siedliska) — dwie kategorie obszarów Natura 2000.' },
      { term: 'PSE', definition: 'Polskie Sieci Elektroenergetyczne — operator sieci przesyłowej (TSO).' },
      { term: 'ISOK', definition: 'Informatyczny System Osłony Kraju — mapy zagrożenia powodziowego Q10/Q100/Q500 (KZGW).' },
    ],
    legalReference: 'Obowiązujące ramy prawne: Ustawa o ochronie przyrody (16.04.2004, Natura 2000), Ustawa o planowaniu i zagospodarowaniu przestrzennym (27.03.2003, MPZP), Ustawa o udostępnianiu informacji o środowisku i jego ochronie, udziale społeczeństwa w ochronie środowiska oraz o ocenach oddziaływania na środowisko (3.10.2008, OOŚ), Prawo energetyczne (10.04.1997), Prawo wodne (20.07.2017, dyrektywa 2007/60/WE) oraz Prawo geologiczne i górnicze (9.06.2011).',
    ctaPrimary: 'Zacznij za darmo',
    ctaSecondary: 'Poproś o demo',
    signupUrl: 'https://aplicacion.geovis.es/auth/signup?source=landing_pl',
  },

  // ─────────────────────────────────────────────────────────────────────────
  cz: {
    code: 'cz',
    hreflang: 'cs-CZ',
    path: '/cz',
    flag: '🇨🇿',
    countryName: 'Česko',
    countryNative: 'Česká republika',
    languageLabel: 'Čeština',
    coverageStatus: 'completa',
    modulesCount: 39,
    title: 'GeoVis Česko · GIS prohlížeč pro územní analýzu a proveditelnost projektů | ČÚZK + Natura 2000 + ČGS',
    description:
      'GIS prohlížeč pro územní analýzu v České republice: správní hranice a katastr ČÚZK/RÚIAN, Natura 2000 AOPK (1 111 EVL + 42 PO), zvláště chráněná území (NP, CHKO, ZCHÚ, Ramsar), geologie ČGS, hydrografie DIBAVOD (VÚV TGM), povodně CENIA, těžba a poddolovaná území, doprava ZABAGED a památková péče NPÚ. Více než 39 modulů oficiálních českých vrstev v jedné mapě.',
    badge: '7 dní zdarma · Bez karty',
    h1Lead: 'Územní analýza a',
    h1Highlight: 'proveditelnost projektů',
    h1Tail: 'v České republice',
    subtitle:
      'GIS prohlížeč, který spojuje katastr a hranice ČÚZK/RÚIAN, Natura 2000 a zvláště chráněná území AOPK, geologii a geohazardy ČGS, hydrografii DIBAVOD, povodně CENIA, těžbu, dopravu ZABAGED, elektrárny a památkovou péči NPÚ. Pro územní plánování, územní due diligence, obnovitelné zdroje, inženýring a environmentální poradenství.',
    coverageBlurb:
      'Celostátní pokrytí s více než 39 synchronizovanými moduly oficiálních českých vrstev. Prohlížeč sjednocuje data ČÚZK (RÚIAN, Katastr nemovitostí, ZABAGED), AOPK ČR (Natura 2000, ZCHÚ, ÚSES), ČGS (geologie GEOČR 500, těžba, geohazardy, radon, půdy, hydrogeologie), VÚV TGM DIBAVOD, CENIA (povodně), ČHMÚ (stanice), NPÚ (památková péče), CENIA/MŽP (IRZ E-PRTR, EU-ETS/IPPC) a Copernicus/EEA (Urban Atlas, HRL) s nástroji pro průnik vrstev, izochrony, vyhledávání POI a profesionální export.',
    sectors: [
      { name: 'Územní plánování', body: 'Hranice RÚIAN, katastrální území, Urban Atlas a hustota obyvatelstva ze sčítání lidu 2021.' },
      { name: 'Územní due diligence', body: 'Rychlé prověření omezení: Natura 2000, zvláště chráněná území, poddolovaná území, povodně a CHLÚ ještě před uzavřením opce.' },
      { name: 'Obnovitelné zdroje (utility FVE · větrné elektrárny)', body: 'Průnik s Natura 2000, ZCHÚ, geotermální energií, energetickým atlasem a přenosovou soustavou.' },
      { name: 'Environmentální poradenství', body: 'Natura 2000 (EVL + PO), ZCHÚ, ÚSES, CHOPAV, zranitelnost zvodní a emise IRZ/E-PRTR.' },
      { name: 'Stavební inženýring', body: 'Geologie ČGS, inženýrskogeologické rajony, náchylnost ke svahovým deformacím, radon a poddolovaná území.' },
      { name: 'Těžba a nerostné suroviny', body: 'Ložiska, CHLÚ, dobývací prostory, minerální vody a geotermální energie.' },
      { name: 'Developerské projekty', body: 'Katastrální území, zpevněné plochy, strategický hluk a kulturní památková péče NPÚ.' },
      { name: 'Veřejná správa', body: 'Odborné posouzení záměrů s dohledatelností podle vrstvy a oficiálního zdroje.' },
    ],
    sourcesHeading: 'Integrované oficiální zdroje',
    sources: [
      { name: 'ČÚZK — RÚIAN / INSPIRE', desc: 'Správní hranice (kraje, okresy), katastrální území (13 075) a dopravní síť ZABAGED.' },
      { name: 'AOPK ČR', desc: 'Natura 2000 (1 111 EVL + 42 PO), zvláště chráněná území (NP, CHKO, ZCHÚ, Ramsar), ÚSES, biosférické rezervace UNESCO a geoparky.' },
      { name: 'ČGS — Česká geologická služba', desc: 'Geologie GEOČR 500 a 1:25 000, těžba, geohazardy, radon, půdy TKSP, hydrogeologie a geotermální energie.' },
      { name: 'VÚV TGM — DIBAVOD', desc: 'Vodní síť: povodí, vodní toky (8 171), nádrže a rybníky (70 409).' },
      { name: 'CENIA', desc: 'Mapa povodňového nebezpečí 2019 (směrnice 2007/60/ES) a brownfieldy SEKM.' },
      { name: 'ČHMÚ', desc: 'Síť meteorologických stanic (757 aktivních stanic).' },
      { name: 'NPÚ — Národní památkový ústav', desc: 'Kulturní památková péče: NKP, památkové rezervace, památkové zóny, UNESCO a jednotlivé památky.' },
      { name: 'MŽP / CENIA — IRZ', desc: 'Registr emisí E-PRTR (IRZ) a provozovny EU-ETS / IPPC.' },
      { name: 'Copernicus / EEA', desc: 'Urban Atlas 2018 (15 funkčních městských oblastí) a vrstvy HRL zpevněných ploch a stromového pokryvu.' },
      { name: 'JRC / WRI', desc: 'Elektrárny ≥1 MW (solární, uhelné, vodní, plynové, jaderné).' },
    ],
    modules: {
      cadastral: {
        label: 'Katastr a správní členění',
        items: [
          'Správní hranice RÚIAN — kraje (14) + okresy (77) (ČÚZK INSPIRE AU)',
          'Katastrální území (13 075) (ČÚZK INSPIRE CP)',
          'Hustota obyvatelstva — mřížka 1 km, sčítání lidu 2021 (ČSÚ INSPIRE)',
        ],
      },
      urbanismo: {
        label: 'Územní plánování a využití území',
        items: [
          'Urban Atlas 2018 — 15 funkčních městských oblastí (Copernicus / EEA)',
          'Zpevněné plochy HRL 2018 (Copernicus / EEA)',
          'Stromový pokryv HRL 2018 (Copernicus / EEA)',
          'Kulturní památková péče NPÚ — NKP, rezervace, zóny, UNESCO',
        ],
      },
      territorial: {
        label: 'Geologie, půdy a geohazardy',
        items: [
          'Geologie GEOČR 500 (1:500 000) a mapa 1:25 000 — ČGS',
          'Těžba — ložiska, CHLÚ, dobývací prostory, průzkumná území (ČGS / ČBÚ)',
          'Geohazardy — radon, svahové deformace, staré důlní dílo (ČGS)',
          'Poddolovaná území (bloker rizika poklesů)',
          'Náchylnost ke svahovým deformacím 1:25 000 + inženýrskogeologické rajony',
          'Půdy TKSP (1:50 000), geotermální energie, kvartér, paleontologie a geologické lokality',
        ],
      },
      infrastructure: {
        label: 'Infrastruktura a průmysl',
        items: [
          'Doprava ZABAGED — dálnice + silnice I/II (22 639) + železnice SŽ (5 248)',
          'Elektrárny ≥1 MW — solární, uhelné, vodní, plynové, jaderné (JRC/WRI)',
          'Emise EU-ETS + IPPC (MŽP IRZ)',
          'Průmysl IRZ / E-PRTR + brownfieldy SEKM (CENIA)',
        ],
      },
      environment: {
        label: 'Životní prostředí a památková péče',
        items: [
          'Soustava Natura 2000 — 1 111 EVL (přírodní stanoviště) + 42 PO (ptačí oblasti) (AOPK)',
          'Zvláště chráněná území — NP, CHKO, ZCHÚ (NPR/NPP/PR/PP), Ramsar (AOPK)',
          'ÚSES, památné stromy, biosférické rezervace UNESCO a geoparky (AOPK)',
          'Strategický hluk SHM2022 — silnice, železnice, letecký provoz, průmysl',
        ],
      },
      hydrography: {
        label: 'Hydrografie a vody',
        items: [
          'Vodní síť DIBAVOD — povodí, vodní toky (8 171), nádrže (70 409) (VÚV TGM)',
          'Povodně CENIA 2019 — nebezpečí residuální/nízké/střední/vysoké (směrnice 2007/60/ES)',
          'Minerální a termální vody (190 jímacích území) — ČGS',
          'Hydrogeologické rajony, základní odtok a zranitelnost zvodní (ČGS)',
        ],
      },
    },
    restrictionsHeading: 'Zjištěná blokující omezení',
    restrictions: [
      { name: 'Soustava Natura 2000 (EVL + PO)', body: '1 153 lokalit podmiňuje nebo blokuje projekty podle plánu péče.' },
      { name: 'Zvláště chráněná území (NP, CHKO, ZCHÚ)', body: 'Národní parky a chráněné krajinné oblasti s omezeným režimem využití.' },
      { name: 'CHLÚ — chráněné ložiskové území', body: 'Chráněná ložisková území blokují výstavbu, pokud chybí souhlas orgánu státní báňské správy.' },
      { name: 'Poddolovaná území', body: 'Riziko poklesů kvůli historickým důlním dílům a šachtám — právní blokátor výstavby.' },
      { name: 'Povodně CENIA', body: 'Střední a vysoké nebezpečí ovlivňuje pojištění a bankovní financování.' },
      { name: 'CHOPAV a OPPLZ', body: 'Chráněné oblasti přirozené akumulace vod a ochranná pásma přírodních léčivých zdrojů s dalšími omezeními.' },
      { name: 'Kulturní památková péče NPÚ', body: 'Památkové rezervace a zóny, NKP a statky UNESCO s ochrannými pásmy.' },
      { name: 'Radonové riziko', body: 'Vysoký radonový index vyžaduje stavební opatření podle atomového zákona č. 263/2016 Sb.' },
    ],
    regulatoryFramework: [
      { norma: 'Zákon 114/1992 Sb.', scope: 'Ochrana přírody a krajiny — Natura 2000, ZCHÚ.' },
      { norma: 'Zákon 183/2006 Sb.', scope: 'Územní plánování a stavební řád (stavební zákon).' },
      { norma: 'Zákon 100/2001 Sb.', scope: 'Posuzování vlivů na životní prostředí (EIA / SEA).' },
      { norma: 'Zákon 254/2001 Sb.', scope: 'Vodní zákon — CHOPAV, záplavová území.' },
      { norma: 'Zákon 44/1988 Sb.', scope: 'Horní zákon — CHLÚ, dobývací prostory.' },
      { norma: 'Zákon 20/1987 Sb.', scope: 'Státní památková péče.' },
      { norma: 'Zákon 164/2001 Sb.', scope: 'Lázeňský zákon — přírodní léčivé zdroje (OPPLZ).' },
    ],
    faq: [
      { q: 'Nahrazuje GeoVis QGIS nebo ArcGIS?', a: 'Ne. GeoVis je prohlížeč pro rychlé prověření a due diligence s přednačtenými a synchronizovanými oficiálními českými vrstvami. QGIS a ArcGIS zůstávají nezbytné pro pokročilé geoprocesování, náročná rastrová data nebo vlastní ETL. Většina týmů používá GeoVis pro rychlé prověření a report a QGIS jen na finální detail.' },
      { q: 'Jaké pokrytí má Česko?', a: 'Celostátní pokrytí s více než 39 moduly oficiálních vrstev: katastr a hranice ČÚZK/RÚIAN, Natura 2000 a zvláště chráněná území AOPK, geologie a geohazardy ČGS, hydrografie DIBAVOD, povodně CENIA, těžba, doprava ZABAGED, elektrárny, památková péče NPÚ a Urban Atlas.' },
      { q: 'Obsahuje katastr parcel?', a: 'Prohlížeč zahrnuje katastrální území (13 075 polygonů) z ČÚZK. Vyhledávání jednotlivých parcel z Katastru nemovitostí probíhá přes WMS službu ČÚZK.' },
      { q: 'Hodí se pro posouzení proveditelnosti FVE nebo větrných elektráren?', a: 'Ano. Prohlížeč kombinuje Natura 2000, zvláště chráněná území, poddolovaná území, povodně, geologii a dopravní síť pro prověření omezení, a navíc geotermální energii a energetický atlas.' },
      { q: 'Jak často se vrstvy aktualizují?', a: 'Synchronizace probíhá podle publikačních cyklů jednotlivých oficiálních českých zdrojů (ČÚZK, AOPK, ČGS, VÚV TGM, CENIA, ČHMÚ, NPÚ). Každá vrstva zobrazuje svůj původ a odkaz na zdroj.' },
      { q: 'Mohu exportovat to, co vidím na mapě?', a: 'Ano, v plánu Pro a Premium: KMZ, GeoJSON, Shapefile a PDF. Report pay-per-use zahrnuje vrstvy zkřížené s vymezeným územím a příslušný legislativní rámec.' },
    ],
    glossary: [
      { term: 'ČÚZK', definition: 'Český úřad zeměměřický a katastrální — úřad pro zeměměřictví a katastr nemovitostí.' },
      { term: 'RÚIAN', definition: 'Registr územní identifikace, adres a nemovitostí.' },
      { term: 'AOPK ČR', definition: 'Agentura ochrany přírody a krajiny České republiky.' },
      { term: 'EVL / PO', definition: 'Evropsky významné lokality (přírodní stanoviště) / Ptačí oblasti — soustava Natura 2000.' },
      { term: 'ZCHÚ', definition: 'Zvláště chráněná území — kategorie NP, CHKO, NPR, NPP, PR, PP.' },
      { term: 'ČGS', definition: 'Česká geologická služba.' },
      { term: 'CHLÚ', definition: 'Chráněné ložiskové území — blokuje výstavbu bez souhlasu báňského úřadu.' },
      { term: 'CHOPAV', definition: 'Chráněné oblasti přirozené akumulace vod.' },
    ],
    legalReference: 'Použitelný rámec: zákon 114/1992 Sb. (ochrana přírody a krajiny — Natura 2000), zákon 100/2001 Sb. (EIA), zákon 183/2006 Sb. (územní plánování a stavební řád), zákon 254/2001 Sb. (vodní zákon), zákon 44/1988 Sb. (horní zákon), zákon 20/1987 Sb. (státní památková péče), zákon 164/2001 Sb. (přírodní léčivé zdroje).',
    ctaPrimary: 'Začít zdarma',
    ctaSecondary: 'Vyžádat demo',
    signupUrl: 'https://aplicacion.geovis.es/auth/signup?source=landing_cz',
  },

  // ─────────────────────────────────────────────────────────────────────────
  uk: {
    code: 'uk',
    hreflang: 'en-GB',
    path: '/uk',
    flag: '🇬🇧',
    countryName: 'United Kingdom',
    countryNative: 'United Kingdom',
    languageLabel: 'English',
    coverageStatus: 'completa',
    modulesCount: 95,
    title: 'GeoVis United Kingdom · GIS Viewer for Land Analysis and Project Feasibility | Ordnance Survey + Environment Agency + Natural England',
    description:
      'GIS viewer for land analysis in the United Kingdom: ONS boundaries (4 nations · 361 LADs), Defra protected areas (SAC, SPA, Ramsar, SSSI, AONB, National Parks), ancient woodland, Scheduled Monuments and Listed Buildings (Historic England · HES · Cadw · NIEA), Environment Agency flood zones, the National Grid network, BGS geology, Coal Authority data and REPD renewable energy projects. Coverage across England, Scotland, Wales and Northern Ireland.',
    badge: '7-day free trial · No card required',
    h1Lead: 'Land analysis and',
    h1Highlight: 'project feasibility',
    h1Tail: 'in the United Kingdom',
    subtitle:
      'A GIS viewer with ONS administrative boundaries, Defra protected areas (SAC, SPA, Ramsar, SSSI), the Environment Agency Flood Map for Planning, the National Grid transmission network, BGS geology, Coal Authority data, Historic England heritage assets and the REPD renewable energy planning database. Built for due diligence, planning, renewables, infrastructure and environmental consultancy.',
    coverageBlurb:
      'Full coverage of all four nations: England, Scotland, Wales and Northern Ireland, with the relevant devolved bodies integrated (Natural England, NatureScot, Natural Resources Wales and NIEA/DAERA for the environment; Historic England, HES, Cadw and NIEA for heritage). The viewer brings together Ordnance Survey, ONS Open Geography, the Defra Data Services Platform, Environment Agency, National Grid, NSTA, British Geological Survey, Coal Authority and DESNZ in a single map, with professional overlay, search and export tools.',
    sectors: [
      { name: 'Renewables (solar · onshore/offshore wind · BESS)', body: 'Overlay with REPD, National Grid/NESO/DNO grid capacity, wind and solar atlases, Crown Estate offshore areas and environmental constraints.' },
      { name: 'Land due diligence', body: 'Rapid screening of land portfolios: flood zones, protected areas, heritage assets and Green Belt before options are agreed.' },
      { name: 'Planning', body: 'Flood Map for Planning, Green Belt, Conservation Areas, Listed Buildings, Scheduled Monuments and Ancient Woodland as material considerations under the NPPF.' },
      { name: 'Environmental consultancy', body: 'SAC, SPA, Ramsar, SSSI, Priority Habitats, Nutrient Neutrality and Biodiversity Net Gain (LNRS) for EIA and Habitats Regulations Assessment.' },
      { name: 'Infrastructure and engineering', body: 'OS roads, Network Rail, SRN, HS2, NaPTAN, the electricity network and aeronautical safeguarding (ATZ · restricted airspace).' },
      { name: 'Property development', body: 'OS built-up areas, Green Belt, contaminated land, Coal Mining Risk Areas and multiple deprivation (IMD).' },
      { name: 'Agriculture and land', body: 'Agricultural Land Classification (Best and Most Versatile), Countryside Stewardship, Organic Farming and Nitrate Sensitive Areas.' },
      { name: 'Offshore energy and hydrocarbons', body: 'NSTA UKCS (licences, fields, wells, pipelines, CCS), ScotWind/INTOG, Crown Estate and Marine Protected Areas.' },
    ],
    sourcesHeading: 'Official data sources',
    sources: [
      { name: 'Ordnance Survey (OS)', desc: 'OS Open Roads, OS Open Rivers, OS Open Greenspace and built-up areas. Licensed under OGL v3.' },
      { name: 'ONS Open Geography', desc: 'UK-wide administrative boundaries: 4 nations, 9 regions, 361 Local Authority Districts, 11,337 parishes and TTWAs.' },
      { name: 'Environment Agency (EA)', desc: 'Flood Map for Planning (FZ3), RoFRS, RoFSW, flood defences, Source Protection Zones, Main River and WFD status.' },
      { name: 'Natural England / Defra DSP', desc: 'SAC, SPA, Ramsar, SSSI, National Parks, AONB, NNR/LNR, Ancient Woodland, Nutrient Neutrality, ALC and Priority Habitats.' },
      { name: 'NatureScot · NRW · NIEA/DAERA', desc: 'Devolved protected-area designations for Scotland, Wales and Northern Ireland (SAC, SPA, SSSI/ASSI, NNR, AONB, priority peatland).' },
      { name: 'Historic England · HES · Cadw · NIEA', desc: 'Listed Buildings, Scheduled Monuments, Conservation Areas, Registered Parks & Gardens, battlefields and World Heritage Sites.' },
      { name: 'National Grid · NESO · DNOs', desc: 'Transmission network (E+W), Grid Supply Points, DNO areas, headroom and distribution substations.' },
      { name: 'DESNZ · NSTA · Crown Estate', desc: 'REPD (renewables in planning), offshore hydrocarbons and CCS (UKCS), offshore wind Rounds 1-6, ScotWind and INTOG.' },
      { name: 'British Geological Survey (BGS)', desc: '1:625k geology (bedrock + superficial), aquifer designations and the National Landslide Database.' },
      { name: 'Coal Authority', desc: 'Coal Mining Risk Areas (high/low risk), coal licences and mandatory consultation zones.' },
    ],
    modules: {
      cadastral: {
        label: 'Administrative boundaries and land',
        items: [
          'ONS Open Geography — 4 nations + 9 regions + 361 Local Authority Districts + 11,337 parishes',
          'OS Open Built-Up Areas — built-up areas across GB (Ordnance Survey)',
          'Travel-to-Work Areas (TTWA) — functional labour markets',
          'Multiple deprivation across all 4 nations (IMD/SIMD/WIMD/NIMDM) by decile',
        ],
      },
      urbanismo: {
        label: 'Planning and public access',
        items: [
          'Green Belt England — 16 green belts (NPPF restriction)',
          'Public access under the CRoW Act 2000 — National Trails, open country, common land',
          'England Coast Path — National Trail ~4,500 km + margin',
          'CRoW Section 15/16 — voluntary and dedicated access',
          'Listed Buildings and Conservation Areas as material considerations',
        ],
      },
      territorial: {
        label: 'Land, agriculture and renewables',
        items: [
          'Agricultural Land Classification (ALC) — Best and Most Versatile (Grade 1, 2, 3a)',
          'Countryside Stewardship / ESS / HLS / SFI — agri-environment parcels under agreement',
          'Organic Farming Scheme and Nitrate Sensitive Areas',
          'REPD (DESNZ) — 14,278 renewable energy projects with planning status',
          '100m wind atlas and solar atlas (PVOUT) — Global Wind/Solar Atlas',
        ],
      },
      infrastructure: {
        label: 'Infrastructure and energy',
        items: [
          'OS Open Roads + SRN Strategic Road Network + HS2 + WebTRIS traffic counts',
          'Network Rail — GB tracks and nodes + NaPTAN public transport + EV charging',
          'National Grid transmission network (E+W) — substations, lines, pylons, gas',
          'NESO Grid Supply Points + DNO areas + Northern Powergrid / SSEN networks',
          'Nuclear, HAR1/HAR2 hydrogen, CCUS clusters and HVDC interconnectors',
          'NSTA UKCS — licences, fields, wells, pipelines and offshore CCS',
          'MOD military estate + restricted airspace (D/R/P/MATZ) + civil ATZs + airports',
        ],
      },
      environment: {
        label: 'Environment and heritage',
        items: [
          'SAC, SPA, Ramsar, SSSI, National Parks, AONB, NNR/LNR (UK-wide · all 4 nations)',
          'Ancient Woodland Inventory — irreplaceable habitat (NPPF blocking constraint)',
          'Priority Habitats, LNRS (Biodiversity Net Gain), peat and Woodland Carbon Code',
          'Listed Buildings — 379,672 (Historic England) + HES + Cadw + NIEA',
          'Scheduled Monuments, Registered Parks & Gardens, battlefields and World Heritage Sites',
          'AQMA and CSF — air quality (NO2/PM10 + agricultural NH3)',
        ],
      },
      hydrography: {
        label: 'Hydrography, flood risk and water',
        items: [
          'EA Flood Map for Planning (Flood Zone 3) + Flood Storage Areas',
          'RoFRS (river/sea flood risk) + RoFSW (surface water flood risk) + defences and reservoirs',
          'Flood risk in Wales (TAN15 · NRW) and Scotland (SEPA)',
          'Source Protection Zones, Nutrient Neutrality and Main River (EA)',
          'Chalk Rivers, Priority River Habitat and WFD status (rivers, lakes, coastal, groundwater)',
          'Contaminated land, historic landfill sites and permitted waste sites (EA)',
        ],
      },
    },
    restrictionsHeading: 'Blocking constraints detected',
    restrictions: [
      { name: 'Flood Zone 3 (EA Flood Map for Planning)', body: 'The NPPF baseline is "do not develop"; it affects financing and insurance. RoFRS/RoFSW provide additional screening.' },
      { name: 'Ancient Woodland and Veteran Trees', body: 'A blocking constraint unless "wholly exceptional reasons" apply under the NPPF; affects ASNW, PAWS and wood pasture.' },
      { name: 'Scheduled Monuments', body: 'Any impact requires Scheduled Monument Consent from the Secretary of State (Ancient Monuments Act 1979).' },
      { name: 'Listed Buildings and Conservation Areas', body: 'Listed Building Consent is mandatory; the setting is a material consideration in planning decisions.' },
      { name: 'Natura 2000 UK (SAC + SPA) and Ramsar', body: 'Requires a Habitats Regulations Assessment under the Conservation of Habitats and Species Regulations 2017.' },
      { name: 'Nutrient Neutrality Catchments', body: '39 catchments where Natural England blocks residential development unless nitrogen and phosphorus neutrality is demonstrated.' },
      { name: 'Coal Mining Risk Areas', body: 'A High Risk Area requires a Coal Mining Risk Assessment from the Coal Authority as part of planning.' },
      { name: 'Green Belt', body: 'A presumption against development unless "very special circumstances" apply (NPPF).' },
      { name: 'Source Protection Zones (SPZ)', body: 'A blocking constraint for industrial/data centre development near abstraction points, under the Groundwater Regulations.' },
      { name: 'Best and Most Versatile Land (ALC)', body: 'Grades 1, 2 and 3a are protected under the NPPF and EN-1 against the loss of high-quality agricultural land.' },
    ],
    regulatoryFramework: [
      { norma: 'NPPF (National Planning Policy Framework)', scope: 'England\'s planning policy framework — Green Belt, flood risk, heritage, BMV land.' },
      { norma: 'Town and Country Planning Act 1990', scope: 'The planning permission and development regime in England and Wales.' },
      { norma: 'Conservation of Habitats and Species Regulations 2017', scope: 'SAC, SPA, Habitats Regulations Assessment and nutrient neutrality.' },
      { norma: 'Wildlife and Countryside Act 1981', scope: 'SSSI, Limestone Pavement Orders and species protection.' },
      { norma: 'Ancient Monuments and Archaeological Areas Act 1979', scope: 'Scheduled Monuments and Scheduled Monument Consent.' },
      { norma: 'Planning (Listed Buildings and Conservation Areas) Act 1990', scope: 'Listed Buildings, Conservation Areas and their setting.' },
      { norma: 'Environment Act 2021', scope: 'Biodiversity Net Gain, Local Nature Recovery Strategies (LNRS).' },
      { norma: 'Water Resources Act 1991', scope: 'Statutory Main River and Environment Agency powers.' },
    ],
    faq: [
      { q: 'Does GeoVis cover all four nations of the United Kingdom?', a: 'Yes. England, Scotland, Wales and Northern Ireland, with the relevant devolved bodies integrated: Natural England, NatureScot, Natural Resources Wales and NIEA/DAERA for the environment; Historic England, HES, Cadw and NIEA for heritage. The federated UK-wide layers (SSSI, Ramsar, MPA) cover all four jurisdictions through a single endpoint.' },
      { q: 'Is it suitable for planning due diligence in England?', a: 'Yes. This is one of the primary use cases: the Environment Agency Flood Map for Planning, Green Belt, Conservation Areas, Listed Buildings, Scheduled Monuments and Ancient Woodland are all material considerations under the NPPF, and they come pre-loaded and kept in sync.' },
      { q: 'Which official sources does it integrate for renewables?', a: 'The DESNZ Renewable Energy Planning Database (REPD) with the planning status of each project, the National Grid transmission network, NESO Grid Supply Points, DNO areas with headroom, the Crown Estate offshore leasing rounds (Round 1-6, ScotWind, INTOG) and the wind and solar atlases.' },
      { q: 'Does GeoVis replace QGIS or ArcGIS?', a: 'No. GeoVis is a screening and due diligence viewer with the official UK layers pre-loaded. QGIS and ArcGIS are still needed for advanced geoprocessing, heavy raster work or custom ETL. Most teams use GeoVis for rapid screening and reporting.' },
      { q: 'Does it cover flood risk beyond the Flood Map for Planning?', a: 'Yes. Alongside Flood Zone 3, it integrates RoFRS (river and sea flood risk across 4 bands), RoFSW (1-in-100 surface water flood risk), Environment Agency defences and reservoirs, and flood risk data for Wales (TAN15 · NRW) and Scotland (SEPA).' },
      { q: 'Does it include geology and coal mining risk?', a: 'Yes. BGS 1:625k geology (bedrock and superficial), aquifer designations, the National Landslide Database, and the Coal Authority\'s Coal Mining Risk Areas, which trigger a Coal Mining Risk Assessment in the planning process.' },
    ],
    glossary: [
      { term: 'NPPF', definition: 'National Planning Policy Framework — England\'s planning policy framework.' },
      { term: 'SSSI', definition: 'Site of Special Scientific Interest — a nature protection designation (Wildlife and Countryside Act 1981).' },
      { term: 'SAC / SPA / Ramsar', definition: 'Special Area of Conservation / Special Protection Area / Ramsar wetland — an international protection network.' },
      { term: 'AONB', definition: 'Area of Outstanding Natural Beauty — a protected landscape (now National Landscape).' },
      { term: 'Listed Building', definition: 'A Grade I/II*/II listed building (Historic England, HES, Cadw, NIEA).' },
      { term: 'Scheduled Monument', definition: 'An archaeological monument protected under the Ancient Monuments Act 1979.' },
      { term: 'Flood Zone 3', definition: 'The highest flood probability zone on the EA Flood Map for Planning — the "do not develop" baseline.' },
      { term: 'REPD', definition: 'Renewable Energy Planning Database (DESNZ) — renewable energy projects with their planning status.' },
      { term: 'BMV Land', definition: 'Best and Most Versatile Land — Grade 1, 2 and 3a agricultural land protected under the NPPF.' },
      { term: 'OGL v3', definition: 'Open Government Licence v3 — the open licence for official UK data.' },
    ],
    legalReference: 'Applicable framework: NPPF (National Planning Policy Framework), Town and Country Planning Act 1990, Conservation of Habitats and Species Regulations 2017, Wildlife and Countryside Act 1981, Ancient Monuments and Archaeological Areas Act 1979, Planning (Listed Buildings and Conservation Areas) Act 1990, Environment Act 2021, Water Resources Act 1991. Data licensed under the Open Government Licence v3.',
    ctaPrimary: 'Start for free',
    ctaSecondary: 'Request a demo',
    signupUrl: 'https://aplicacion.geovis.es/auth/signup?source=landing_uk',
  },

  // ─────────────────────────────────────────────────────────────────────────
  mx: {
    code: 'mx',
    hreflang: 'es-MX',
    path: '/mx',
    flag: '🇲🇽',
    countryName: 'México',
    countryNative: 'México',
    languageLabel: 'Español',
    coverageStatus: 'completa',
    modulesCount: 50,
    title: 'GeoVis México · Visor GIS para análisis territorial y viabilidad de proyectos | Núcleos agrarios RAN + ANP CONANP + Marco Geoestadístico INEGI',
    description:
      'Visor GIS web para análisis territorial en México: núcleos agrarios RAN (30.532 ejidos y comunidades), áreas naturales protegidas CONANP, marco geoestadístico INEGI (32 estados · 2.475 municipios), hidrografía y cuencas CONAGUA, concesiones mineras, red eléctrica y POET/POEGT ambientales. Cribado de afecciones y due diligence en un solo mapa.',
    badge: 'Cobertura nacional · Sin tarjeta',
    h1Lead: 'Análisis territorial y',
    h1Highlight: 'viabilidad de proyectos',
    h1Tail: 'en México',
    subtitle:
      'Visor GIS con núcleos agrarios RAN, áreas naturales protegidas CONANP, marco geoestadístico INEGI, hidrografía y cuencas CONAGUA, concesiones mineras, red eléctrica y ordenamiento ecológico POET/POEGT. Para energías renovables, due diligence, infraestructura, ambiental y promoción de suelo.',
    coverageBlurb:
      'Cobertura nacional con más de 50 grupos de capas oficiales sincronizadas. El diferenciador en México es la tenencia social del suelo: el visor precarga los 30.532 núcleos agrarios certificados (ejidos y comunidades) del RAN, que cubren más de la mitad del territorio nacional, junto al marco geoestadístico INEGI, las ANP de CONANP, la hidrografía y cuencas de CONAGUA, la geología, los peligros geológicos y sísmicos, el ordenamiento ecológico SEMARNAT y la red eléctrica, todo cruzable en un mismo mapa con herramientas de análisis y exportación.',
    sectors: [
      { name: 'Energías renovables (FV · eólica)', body: 'Atlas AZEL de potencial solar/eólico/geotérmico, subestaciones, exclusión y cruce con núcleos agrarios y ANP.' },
      { name: 'Due diligence territorial', body: 'Cribado de afecciones críticas (RAN, ANP, POET) antes de cerrar la compra o la opción de suelo.' },
      { name: 'Tenencia y propiedad social', body: 'Núcleos agrarios RAN (ejidos y comunidades) y reservas territoriales: la tierra se negocia con la asamblea ejidal.' },
      { name: 'Consultoría ambiental', body: 'Cruce con ANP CONANP, biodiversidad CONABIO, POET/POEGT y proyectos SIGEIA para evaluación de impacto.' },
      { name: 'Infraestructura e ingeniería', body: 'Red Nacional de Caminos, red ferroviaria ARTF, hidrografía y distritos de riego CONAGUA.' },
      { name: 'Minería y geología', body: 'Concesiones mineras vigentes, geología, fallas y peligros geomorfológicos.' },
      { name: 'Gestión de riesgos', body: 'Sismos SSN, ciclones tropicales, peligros geológicos y zonas de marginación social.' },
      { name: 'Administración pública', body: 'Evaluación técnica de iniciativas con trazabilidad a fuentes federales.' },
    ],
    sourcesHeading: 'Fuentes oficiales integradas',
    sources: [
      { name: 'RAN — Registro Agrario Nacional', desc: 'Núcleos agrarios certificados (30.532 ejidos y comunidades) y reservas territoriales (11.109).' },
      { name: 'INEGI', desc: 'Marco geoestadístico (32 estados · 2.475 municipios), uso de suelo y vegetación, edafología.' },
      { name: 'CONANP / SEMARNAT', desc: 'Áreas naturales protegidas, ADVC, sitios Ramsar y zonas núcleo.' },
      { name: 'CONAGUA', desc: 'Hidrografía, cuencas, disponibilidad de agua, acuíferos, distritos de riego y PTAR.' },
      { name: 'SEMARNAT / CONABIO', desc: 'Biodiversidad, sitios prioritarios, manglares, UMA, ordenamiento ecológico POET/POEGT y SIGEIA.' },
      { name: 'SENER / CFE', desc: 'Centrales eléctricas, líneas de transmisión, subestaciones y Atlas AZEL de renovables.' },
      { name: 'SE — Secretaría de Economía', desc: 'Concesiones mineras vigentes (CartoMinMex).' },
      { name: 'INAH / UNESCO', desc: 'Zonas arqueológicas abiertas al público y patrimonio mundial.' },
      { name: 'SSN / UNAM', desc: 'Epicentros sísmicos (Servicio Sismológico Nacional) y neotectónica de fallas.' },
      { name: 'INPI / CONAPO / CONEVAL', desc: 'Regiones y localidades indígenas, índice de marginación y rezago social.' },
    ],
    modules: {
      cadastral: {
        label: 'Tenencia de la tierra y límites',
        items: [
          'Núcleos agrarios RAN — 30.532 ejidos y comunidades (propiedad social)',
          'Reservas territoriales RAN — 11.109 polígonos',
          'Marco geoestadístico INEGI — 32 estados, 2.475 municipios',
          'Sistema Urbano Nacional CONAPO — 1.089 ciudades y zonas metropolitanas',
        ],
      },
      urbanismo: {
        label: 'Ordenamiento ecológico y territorial',
        items: [
          'POET — programas de ordenamiento ecológico, UGAs (SEMARNAT)',
          'POEGT — ordenamiento ecológico general del territorio (145 UABs)',
          'SIGEIA — proyectos con evaluación de impacto ambiental (PEIA)',
        ],
      },
      territorial: {
        label: 'Suelo, geología y riesgos',
        items: [
          'Uso de suelo y vegetación INEGI — 141.265 polígonos',
          'Edafología INEGI — suelo dominante (clasificación WRB)',
          'Geología y fallas neotectónicas (UNAM Terra Digitalis)',
          'Peligros geológicos — vulcanismo, hundimientos, geomorfología',
          'Sismos SSN — epicentros mag ≥ 4.0 desde 1900 (50.184 eventos)',
          'Ciclones tropicales (NOAA IBTrACS) y clima (Köppen-García)',
          'Marginación CONAPO y rezago social CONEVAL por municipio',
          'Regiones y localidades indígenas INPI',
        ],
      },
      infrastructure: {
        label: 'Infraestructura y energía',
        items: [
          'Red Nacional de Caminos INEGI/IMT — 163.685 ejes viales + aeropuertos',
          'Red ferroviaria ARTF — vías, estaciones, túneles y puentes',
          'Centrales eléctricas y líneas de transmisión (SENER/CFE)',
          'Atlas AZEL — subestaciones, potencial eólico/solar/geotérmico, gasoductos',
          'Concesiones mineras vigentes (CartoMinMex)',
          'Industria y contaminación — RETC, sitios de disposición, ductos',
          'PTAR y gestión de residuos (rellenos y confinamientos)',
        ],
      },
      environment: {
        label: 'Ambiente, biodiversidad y patrimonio',
        items: [
          'Áreas naturales protegidas CONANP — ANP, ADVC, Ramsar, zonas núcleo',
          'Biodiversidad CONABIO — sitios prioritarios, ecorregiones, AICAs',
          'RTP, RHP y manglares (CONABIO SNIB)',
          'UMA — Unidades de Manejo para la Conservación de Vida Silvestre',
          'CUSTF y zonas críticas de deforestación (CONAFOR)',
          'Zona Federal Marítimo Terrestre (ZOFEMAT) y regiones marinas',
          'Zonas arqueológicas INAH y patrimonio mundial UNESCO',
        ],
      },
      hydrography: {
        label: 'Hidrografía y aguas',
        items: [
          'Red hidrográfica CONAGUA — ríos, cuerpos de agua, presas',
          'Cuencas hidrológicas — disponibilidad media anual (757 cuencas)',
          'Acuíferos — disponibilidad, sobreexplotación y déficit',
          'Distritos de riego CONAGUA (86) y cenotes/oasis kársticos',
        ],
      },
    },
    restrictionsHeading: 'Restricciones bloqueantes detectadas',
    restrictions: [
      { name: 'Núcleos agrarios RAN', body: 'La propiedad social (ejido/comunidad) exige negociación con la asamblea ejidal, no con un propietario privado.' },
      { name: 'Áreas naturales protegidas (CONANP)', body: 'ANP federales, ADVC y sitios Ramsar bloquean o condicionan proyectos según su programa de manejo.' },
      { name: 'Ordenamiento ecológico (POET/POEGT)', body: 'Las UGAs y UABs fijan políticas (protección, conservación, restauración, aprovechamiento) por polígono.' },
      { name: 'Zona Federal Marítimo Terrestre', body: 'Deslindes ZOFEMAT condicionan cualquier desarrollo costero.' },
      { name: 'Concesiones mineras vigentes', body: 'Una concesión activa puede solapar y bloquear el aprovechamiento del suelo.' },
      { name: 'Peligros geológicos y sísmicos', body: 'Vulcanismo, fallas, hundimientos y zonas sísmicas afectan diseño, seguros y financiación.' },
      { name: 'Disponibilidad de agua por cuenca', body: 'Cuencas "sin disponibilidad" (DOF) impiden nuevas concesiones de aprovechamiento.' },
      { name: 'CUSTF — cambio de uso de suelo forestal', body: 'El aprovechamiento en terreno forestal requiere autorización y compensación ambiental.' },
    ],
    regulatoryFramework: [
      { norma: 'Ley Agraria', scope: 'Régimen de propiedad social — ejidos y comunidades (RAN).' },
      { norma: 'LGEEPA', scope: 'Ley General del Equilibrio Ecológico y la Protección al Ambiente — ANP, EIA, POET.' },
      { norma: 'Ley General de Vida Silvestre', scope: 'UMA y conservación de especies.' },
      { norma: 'Ley de Aguas Nacionales', scope: 'Concesiones, cuencas, acuíferos y ZOFEMAT (CONAGUA).' },
      { norma: 'Ley Minera', scope: 'Concesiones mineras y aprovechamiento del subsuelo.' },
      { norma: 'Ley General de Desarrollo Forestal Sustentable', scope: 'Cambio de uso de suelo forestal (CUSTF) y CONAFOR.' },
    ],
    faq: [
      { q: '¿GeoVis sustituye a QGIS o ArcGIS?', a: 'No. GeoVis es un visor de cribado y due diligence con las capas oficiales mexicanas precargadas y sincronizadas. QGIS y ArcGIS siguen siendo necesarios para geoprocesamiento avanzado o ETL personalizado. La mayoría de los equipos usa GeoVis para el cribado rápido y QGIS solo para el detalle final.' },
      { q: '¿Por qué es clave la capa de núcleos agrarios RAN?', a: 'Porque más de la mitad del territorio nacional es propiedad social. GeoVis precarga los 30.532 ejidos y comunidades certificados del RAN: la tierra no se compra a un propietario privado, sino que se negocia con la asamblea ejidal. Detectarlo a tiempo evita cerrar opciones inviables.' },
      { q: '¿Qué fuentes ambientales integra?', a: 'Áreas naturales protegidas de CONANP (ANP, ADVC, Ramsar), biodiversidad de CONABIO, ordenamiento ecológico POET y POEGT de SEMARNAT, proyectos con evaluación de impacto en SIGEIA, manglares, UMA y zonas críticas de deforestación de CONAFOR.' },
      { q: '¿Sirve para proyectos de renovables?', a: 'Sí. Integra el Atlas AZEL (potencial solar, eólico y geotérmico, subestaciones, exclusión y gasoductos), las centrales y líneas de transmisión, y permite cruzarlos con núcleos agrarios RAN, ANP y disponibilidad de agua por cuenca.' },
      { q: '¿Cobertura de todo el país?', a: 'Sí. Marco geoestadístico INEGI con los 32 estados y 2.475 municipios, más de 50 grupos de capas federales sincronizadas con INEGI, RAN, CONANP, CONAGUA, SEMARNAT, SENER, SE, INAH y SSN.' },
      { q: '¿Puedo exportar lo que veo en el mapa?', a: 'Sí, en los planes superiores: KMZ, GeoJSON, Shapefile y PDF. El informe incluye las capas cruzadas con el perímetro del proyecto y el marco normativo aplicable.' },
    ],
    glossary: [
      { term: 'RAN', definition: 'Registro Agrario Nacional — núcleos agrarios (ejidos y comunidades) de propiedad social.' },
      { term: 'Ejido', definition: 'Núcleo agrario de propiedad social gestionado por asamblea; modo predominante de tenencia en México.' },
      { term: 'ANP', definition: 'Área Natural Protegida — figura de conservación federal administrada por CONANP.' },
      { term: 'POET / POEGT', definition: 'Programa de Ordenamiento Ecológico del Territorio / General del Territorio (SEMARNAT).' },
      { term: 'UGA / UAB', definition: 'Unidad de Gestión Ambiental / Unidad Ambiental Biofísica — unidad de política del ordenamiento ecológico.' },
      { term: 'ZOFEMAT', definition: 'Zona Federal Marítimo Terrestre — franja costera de dominio federal.' },
      { term: 'CUSTF', definition: 'Cambio de Uso de Suelo en Terrenos Forestales — autorización CONAFOR.' },
      { term: 'SIGEIA', definition: 'Sistema de proyectos con evaluación de impacto ambiental (PEIA, DGIRA-SEMARNAT).' },
    ],
    legalReference: 'Marco aplicable: Ley Agraria (propiedad social y RAN), LGEEPA (ANP, evaluación de impacto y ordenamiento ecológico), Ley General de Vida Silvestre (UMA), Ley de Aguas Nacionales (CONAGUA), Ley Minera, Ley General de Desarrollo Forestal Sustentable (CUSTF), normativa estatal y municipal.',
    ctaPrimary: 'Empezar gratis',
    ctaSecondary: 'Solicitar demo',
    signupUrl: 'https://aplicacion.geovis.es/auth/signup?source=landing_mx',
  },

  // ─────────────────────────────────────────────────────────────────────────
  co: {
    code: 'co',
    hreflang: 'es-CO',
    path: '/co',
    flag: '🇨🇴',
    countryName: 'Colombia',
    countryNative: 'Colombia',
    languageLabel: 'Español',
    coverageStatus: 'amplia',
    modulesCount: 65,
    title: 'GeoVis Colombia · Visor GIS para análisis territorial y viabilidad de proyectos | IGAC + UPME + RUNAP + SGC',
    description:
      'Visor GIS web para análisis territorial profesional en Colombia: energías renovables, due diligence de suelo, urbanismo y consultoría ambiental. Cruza límites IGAC/DIVIPOLA, red eléctrica STN y capacidad de conexión UPME, áreas protegidas RUNAP, resguardos indígenas, Reservas Forestales Ley 2ª, geología y amenazas SGC, frontera agrícola y mercado de tierras UPRA, e instrumentos POT. +65 capas oficiales en un solo mapa.',
    badge: '7 días gratis · Sin tarjeta',
    h1Lead: 'Análisis territorial y',
    h1Highlight: 'viabilidad de proyectos',
    h1Tail: 'en Colombia',
    subtitle:
      'Visor GIS con límites administrativos IGAC/DIVIPOLA, red eléctrica STN y capacidad de conexión UPME, recurso solar/eólico/geotérmico, áreas protegidas RUNAP, resguardos indígenas, Reservas Forestales Ley 2ª, geología y amenazas SGC, frontera agrícola y mercado de tierras UPRA, instrumentos POT y +65 capas oficiales. Para renovables, due diligence de suelo, urbanismo, ingeniería y consultoría ambiental.',
    coverageBlurb:
      'Cobertura nacional amplia sobre los 32 departamentos y 1.122 municipios DIVIPOLA. El visor unifica datos del IGAC (límites, red vial, capacidad de uso del suelo, MAGNA-SIRGAS), UPME (Sistema de Transmisión Nacional, capacidad de conexión, Plan 6GW, recurso energético, mapa socioambiental), SGC (geología, amenaza sísmica NSR-10, movimientos en masa, sismos, volcanes, metalogenia, carbón), UPRA (frontera agrícola, ZIDRES, capacidad de uso, conflicto de uso del suelo, mercado de tierras) y PNN/RUNAP, con herramientas de cruce y exportación profesional.',
    sectors: [
      { name: 'Energías renovables (FV · eólica)', body: 'Recurso solar/eólico UPME, capacidad de conexión STN, proyectos Plan 6GW y exclusiones socioambientales.' },
      { name: 'Due diligence de suelo', body: 'Cribado de áreas protegidas RUNAP, resguardos indígenas, Reservas Ley 2ª y conflicto de uso antes de cerrar opciones.' },
      { name: 'Consultoría ambiental', body: 'Cruce de capas para licenciamiento ANLA, ecosistemas estratégicos y zonas de exclusión legal.' },
      { name: 'Ingeniería e infraestructura', body: 'Red vial IGAC, líneas y subestaciones STN, ductos de hidrocarburos y red geodésica MAGNA-SIRGAS.' },
      { name: 'Urbanismo y ordenamiento', body: 'Instrumentos POT/PBOT/EOT por municipio, estado y generación, escala rural/urbana.' },
      { name: 'Agroindustria', body: 'Frontera agrícola nacional, ZIDRES, capacidad de uso del suelo y unidades físicas homogéneas UPRA.' },
      { name: 'Mercado inmobiliario rural', body: 'Mercado de tierras, distribución predial y rangos de precios por municipio (UPRA).' },
      { name: 'Gestión del riesgo', body: 'Amenaza sísmica NSR-10, movimientos en masa, amenaza volcánica y catálogo de sismos SGC.' },
    ],
    sourcesHeading: 'Fuentes oficiales integradas',
    sources: [
      { name: 'IGAC — Instituto Geográfico Agustín Codazzi', desc: 'Límites administrativos DIVIPOLA, red vial nacional, capacidad de uso del suelo, instrumentos POT y red geodésica MAGNA-SIRGAS.' },
      { name: 'UPME — Unidad de Planeación Minero Energética', desc: 'Sistema de Transmisión Nacional (líneas, subestaciones, torres), capacidad de conexión asignada, proyectos Plan 6GW, recurso solar/eólico/geotérmico/hidro/biomasa y mapa socioambiental.' },
      { name: 'SGC — Servicio Geológico Colombiano', desc: 'Mapa geológico 2015, amenaza sísmica NSR-10, movimientos en masa, sismos históricos, volcanes, metalogenia y potencial carbonífero.' },
      { name: 'UPRA — Unidad de Planificación Rural Agropecuaria', desc: 'Frontera agrícola nacional, ZIDRES, capacidad de uso, conflicto de uso del suelo, exclusiones legales y mercado de tierras rurales.' },
      { name: 'PNN / RUNAP', desc: 'Registro Único Nacional de Áreas Protegidas — SINAP (1.525 áreas).' },
      { name: 'MinAmbiente', desc: 'Reservas Forestales Ley 2ª de 1959, ecosistemas estratégicos, manglares y coberturas de la tierra.' },
      { name: 'ANT — Agencia Nacional de Tierras', desc: 'Resguardos indígenas, consejos comunitarios afrodescendientes y zonas de reserva campesina.' },
      { name: 'DANE / DIVIPOLA', desc: 'Codificación territorial: 32 departamentos y 1.122 municipios.' },
    ],
    modules: {
      cadastral: {
        label: 'Catastro y división administrativa',
        items: [
          'Límites administrativos IGAC/DIVIPOLA — 32 departamentos + 1.122 municipios',
          'Mercado de tierras rurales por departamento (UPRA)',
          'Distribución predial rural por municipio (UPRA)',
          'Rangos de precios de tierras rurales (M COP/ha · UPRA)',
        ],
      },
      urbanismo: {
        label: 'Urbanismo y ordenamiento territorial',
        items: [
          'Instrumentos POT/PBOT/EOT por municipio y vigencia (IGAC datos nacionales POT)',
          'Estado detallado del instrumento — adopción, generación, escala rural/urbana, gestión del riesgo',
          'Capacidad de uso del suelo IGAC — clases agrológicas I–VIII',
        ],
      },
      territorial: {
        label: 'Suelo, recurso energético y agricultura',
        items: [
          'Recurso solar 2024, eólico 20 m, geotérmico, hidroenergético y biomasa (UPME)',
          'Frontera agrícola nacional (~40 M ha · Resolución 261/2018) y ZIDRES (Ley 1776/2016)',
          'Conflicto de uso del suelo y cobertura CLC (UPRA)',
          'Unidades Físicas Homogéneas y cobertura agrológica (UPRA · IGAC)',
        ],
      },
      infrastructure: {
        label: 'Infraestructura y energía',
        items: [
          'Red eléctrica STN UPME — líneas, subestaciones y torres del Sistema de Transmisión Nacional',
          'Capacidad de conexión asignada y proyectos de generación Plan 6GW',
          'Expansión STN en convocatoria — líneas y subestaciones',
          'Red vial nacional IGAC (autopistas a caminos)',
          'Ductos de hidrocarburos, infraestructura de procesamiento y plantas de abastecimiento',
          'Zonas No Interconectadas (ZNI) y red geodésica MAGNA-SIRGAS / GNSS GEORED',
        ],
      },
      environment: {
        label: 'Ambiente, restricciones y geociencias',
        items: [
          'Áreas protegidas RUNAP — SINAP (1.525 áreas)',
          'Reservas Forestales Ley 2ª de 1959, ecosistemas estratégicos y manglares',
          'Resguardos indígenas, consejos comunitarios afro y zonas de reserva campesina',
          'Exclusiones legales agrícolas y restricción socioambiental por departamento (UPME/UPRA)',
          'Geología 2015, metalogenia y potencial carbonífero (SGC)',
          'Amenaza sísmica NSR-10, movimientos en masa (SIMMA), sismos históricos y amenaza volcánica (SGC)',
        ],
      },
      hydrography: {
        label: 'Hidrografía y aguas subterráneas',
        items: [
          'Hidrogeología por regiones (SGC)',
          'Geomorfodinámica (SGC)',
          'Potencial hidroenergético por subzona hidrográfica (UPME)',
          'Geotermia SGC 2025 — manantiales, fumarolas y áreas geotérmicas',
        ],
      },
    },
    restrictionsHeading: 'Restricciones bloqueantes detectadas',
    restrictions: [
      { name: 'Áreas protegidas RUNAP (SINAP)', body: 'Las 1.525 áreas del SINAP bloquean o condicionan proyectos industriales y de generación.' },
      { name: 'Resguardos indígenas y consejos comunitarios', body: 'Consulta previa obligatoria (Ley 21/1991, Sentencia T-129/2011) — determinante en La Guajira y Cesar.' },
      { name: 'Reservas Forestales Ley 2ª de 1959', body: 'Sustracción tramitada ante MinAmbiente; cubren cerca del 48% del territorio (zonificación Tipo A/B).' },
      { name: 'Páramos delimitados', body: 'Veta minera y agropecuaria vinculante por Sentencia C-035/2016.' },
      { name: 'Exclusiones legales y conflicto de uso del suelo', body: 'Páramo, SINAP, Ley 2ª y áreas arqueológicas restringen actividades agrícolas e industriales (UPRA).' },
      { name: 'Capacidad de conexión STN', body: 'Sin capacidad asignada en el punto de conexión seleccionado, el proyecto no se conecta (UPME).' },
      { name: 'Amenaza sísmica NSR-10', body: 'Zona Alta/Intermedia/Baja condiciona el diseño sismo-resistente (Decreto 926/2010).' },
      { name: 'Movimientos en masa y amenaza volcánica', body: 'Amenaza alta SGC limita la viabilidad de obras lineales y de generación.' },
    ],
    regulatoryFramework: [
      { norma: 'Ley 388 de 1997', scope: 'Desarrollo territorial — Planes de Ordenamiento Territorial (POT/PBOT/EOT).' },
      { norma: 'Ley 99 de 1993', scope: 'Sistema Nacional Ambiental (SINA) y licenciamiento ambiental (ANLA).' },
      { norma: 'Ley 2ª de 1959', scope: 'Reservas Forestales Nacionales — sustracción ante MinAmbiente.' },
      { norma: 'Ley 21 de 1991', scope: 'Consulta previa a comunidades indígenas y afrodescendientes (Convenio 169 OIT).' },
      { norma: 'Ley 143 de 1994', scope: 'Sector eléctrico — planeación UPME y conexión al STN.' },
      { norma: 'NSR-10 (Decreto 926/2010)', scope: 'Norma de construcción sismo-resistente — zonas de amenaza sísmica.' },
    ],
    faq: [
      { q: '¿GeoVis sustituye a QGIS o ArcGIS?', a: 'No. GeoVis es un visor de cribado y due diligence con las capas oficiales colombianas precargadas y sincronizadas (IGAC, UPME, SGC, UPRA, RUNAP). QGIS y ArcGIS siguen siendo necesarios para geoprocesamiento avanzado o ETL personalizado. La mayoría de equipos usan GeoVis para el cribado rápido y el informe.' },
      { q: '¿Hay catastro predial a nivel de parcela?', a: 'Colombia no cuenta con un servicio nacional de parcelario predial abierto unificado. GeoVis integra el equivalente disponible: límites administrativos IGAC/DIVIPOLA y el mercado de tierras rurales UPRA (distribución predial y rangos de precios por municipio). El catastro multipropósito predial se incorporará a medida que el IGAC lo publique.' },
      { q: '¿Sirve para evaluar proyectos solares y eólicos?', a: 'Sí, es uno de los casos de uso principales: recurso solar/eólico UPME, capacidad de conexión asignada del STN, proyectos del Plan 6GW y las exclusiones socioambientales (RUNAP, resguardos, Reservas Ley 2ª) cruzadas en un solo mapa.' },
      { q: '¿Por qué pesa tanto la consulta previa?', a: 'Los resguardos indígenas (~25% del territorio) y los consejos comunitarios afrodescendientes activan consulta previa obligatoria (Ley 21/1991, Sentencia T-129/2011). En La Guajira y Cesar es el factor determinante de viabilidad para FV y eólica.' },
      { q: '¿Con qué frecuencia se actualizan las capas?', a: 'La sincronización sigue los ciclos de publicación de cada fuente oficial (IGAC, UPME, SGC, UPRA, PNN). UPME actualiza el STN y la capacidad de conexión periódicamente; cada capa muestra su última fecha de actualización.' },
      { q: '¿Puedo exportar lo que veo en el mapa?', a: 'Sí, en plan Pro y Premium: KMZ, GeoJSON, Shapefile y PDF. El informe incluye las capas cruzadas con el perímetro y el marco normativo aplicable.' },
    ],
    glossary: [
      { term: 'DIVIPOLA', definition: 'División Político-Administrativa de Colombia (DANE) — códigos de departamentos y municipios.' },
      { term: 'STN', definition: 'Sistema de Transmisión Nacional — red eléctrica de alta tensión (≥110 kV) planeada por la UPME.' },
      { term: 'RUNAP', definition: 'Registro Único Nacional de Áreas Protegidas — integra el SINAP.' },
      { term: 'Reserva Ley 2ª', definition: 'Reservas Forestales Nacionales de la Ley 2ª de 1959 — suelo restringido (zonificación Tipo A/B).' },
      { term: 'POT / PBOT / EOT', definition: 'Plan / Plan Básico / Esquema de Ordenamiento Territorial según tamaño del municipio (Ley 388/1997).' },
      { term: 'Consulta previa', definition: 'Derecho fundamental de comunidades étnicas a ser consultadas (Ley 21/1991, Convenio 169 OIT).' },
      { term: 'NSR-10', definition: 'Reglamento Colombiano de Construcción Sismo Resistente (Decreto 926/2010).' },
      { term: 'ZIDRES', definition: 'Zonas de Interés de Desarrollo Rural, Económico y Social (Ley 1776/2016).' },
    ],
    legalReference: 'Marco aplicable: Ley 388 de 1997 (ordenamiento territorial), Ley 99 de 1993 (SINA y licenciamiento ambiental), Ley 2ª de 1959 (reservas forestales), Ley 21 de 1991 (consulta previa · Convenio 169 OIT), Ley 143 de 1994 (sector eléctrico), NSR-10 (Decreto 926/2010), Ley 1776 de 2016 (ZIDRES), normativa de las Corporaciones Autónomas Regionales (CAR).',
    ctaPrimary: 'Empezar gratis',
    ctaSecondary: 'Solicitar demo',
    signupUrl: 'https://aplicacion.geovis.es/auth/signup?source=landing_co',
  },

  // ─────────────────────────────────────────────────────────────────────────
  ec: {
    code: 'ec',
    hreflang: 'es-EC',
    path: '/ec',
    flag: '🇪🇨',
    countryName: 'Ecuador',
    countryNative: 'Ecuador',
    languageLabel: 'Español',
    coverageStatus: 'inicial',
    modulesCount: 60,
    earlyAccess: true,
    title: 'GeoVis Ecuador · Visor GIS para análisis territorial y site selection energético | SNAP + Catastro minero + Red eléctrica CNEL',
    description:
      'Visor GIS para análisis territorial y viabilidad de proyectos en Ecuador: áreas protegidas SNAP (81 + Galápagos), catastro minero ARCERNNR (15.430 concesiones), red eléctrica CNEL/CELEC (92 subestaciones SNI + distribución), hidrografía MAATE (cuencas Pfafstetter, embalses, régimen de inundación), aptitud agrícola MAG 1:25k y catastro urbano Quito + Guayaquil. Enfocado en site selection renovable tras la crisis eléctrica.',
    badge: 'Acceso anticipado · En expansión',
    h1Lead: 'Análisis territorial y',
    h1Highlight: 'viabilidad de proyectos',
    h1Tail: 'en Ecuador',
    subtitle:
      'Visor GIS que integra áreas protegidas SNAP, catastro minero ARCERNNR (15.430 concesiones), red eléctrica CNEL/CELEC, cuencas hidrográficas MAATE, aptitud agrícola MAG 1:25k y catastro urbano de Quito y Guayaquil. Pensado para site selection renovable, due diligence territorial e infraestructura en un mercado que licita bloques de generación.',
    coverageBlurb:
      'Cobertura inicial operativa centrada en site selection energético: restricciones ambientales del MAATE (SNAP continental y Galápagos, Bosques y Vegetación Protectora, Patrimonio Forestal, Socio Bosque, Ramsar, reservas de biósfera, zonas intangibles Tagaeri-Taromenane, Áreas de Protección Hídrica), red eléctrica de transmisión y distribución (subestaciones del SNI, líneas 230/138 kV, red CNEL, expansión PME 2024-2033), hidrografía MAATE (demarcaciones, cuencas Pfafstetter, embalses, régimen de inundación, GeoGLOWS), catastro minero ARCERNNR, geología IIGE, riesgos naturales (peligro volcánico, fallas activas, factor Z, tsunami), aptitud y cobertura del suelo MAG, y catastro urbano de Quito y Guayaquil. Toda la infraestructura oficial se replica a CDN propio (las fuentes gubernamentales son frágiles); cada capa muestra su fuente. Roadmap 2026: catastro rural nacional, recurso solar/eólico y zonificación PDOT.',
    sectors: [
      { name: 'Renovables (FV utility · eólica · hidro)', body: 'Cruce con SNAP, catastro minero, red CNEL/CELEC y régimen de inundación. Mercado que licita bloques tras la crisis eléctrica.' },
      { name: 'Due diligence territorial', body: 'Cribado de restricciones ambientales, mineras e indígenas antes de cerrar opciones de suelo.' },
      { name: 'Infraestructura e ingeniería', body: 'Red vial estatal CONGOPE, ferrocarril, aeropuertos, ductos SOTE/OCP e hidrografía IGM/MAATE.' },
      { name: 'Consultoría ambiental', body: 'SNAP, BVP, Patrimonio Forestal, Ramsar, ecosistemas y áreas prioritarias de restauración para estudios de impacto.' },
      { name: 'Minería', body: 'Catastro minero nacional ARCERNNR con estado de trámite, tipo de mineral y desagregación cantonal.' },
      { name: 'Agricultura y suelo', body: 'Aptitud agrícola, cobertura/uso de la tierra y geopedología MAG 1:25k (conflicto agro vs solar).' },
      { name: 'Promoción inmobiliaria urbana', body: 'Catastro de Quito y Guayaquil con valoración del suelo y PUGS 2023-2027.' },
      { name: 'Administración pública', body: 'Evaluación técnica de proyectos con trazabilidad por capa y fuente oficial.' },
    ],
    sourcesHeading: 'Fuentes oficiales integradas o en roadmap',
    sources: [
      { name: 'MAATE', desc: 'Ministerio del Ambiente, Agua y Transición Ecológica — SNAP, BVP, Patrimonio Forestal, Socio Bosque, Ramsar, ecosistemas, hidrografía (ex-SENAGUA) y régimen de inundación.' },
      { name: 'ARCERNNR', desc: 'Agencia de Regulación y Control de Energía y Recursos Naturales No Renovables — catastro minero nacional (15.430 concesiones) y registro eléctrico.' },
      { name: 'CNEL EP / CELEC EP', desc: 'Red de distribución nacional (subestaciones, subtransmisión, MT, transformadores) y transmisión SNI (92 subestaciones, líneas 230/138 kV, expansión PME).' },
      { name: 'IGM', desc: 'Instituto Geográfico Militar — Mapa Base del Ecuador, hidrografía 1:25k, red vial completa, ferrocarril, aeropuertos.' },
      { name: 'MAG', desc: 'Ministerio de Agricultura y Ganadería — aptitud agrícola 2019, cobertura/uso de la tierra 2020 y geopedología 1:25k (CC-BY-SA).' },
      { name: 'IIGE', desc: 'Instituto de Investigación Geológico y Energético — cartas geológicas 1:100k y geositios (patrimonio geológico).' },
      { name: 'INEC', desc: 'Instituto Nacional de Estadística y Censos — provincias, cantones y parroquias (DPA censo 2022).' },
      { name: 'CONGOPE', desc: 'Red vial estatal (corte mayo 2024).' },
      { name: 'GAD Guayaquil / GAD Quito', desc: 'Catastro urbano (predios, valoración del suelo) y PUGS Guayaquil 2023-2027.' },
      { name: 'GeoGLOWS / ECMWF', desc: 'Red de ríos con caudal modelado y forecast por tramo (visor INAMHI).' },
      { name: 'SIGTIERRAS (roadmap)', desc: 'Catastro rural nacional y MDT 1:25k — pendiente de restablecer infraestructura.' },
      { name: 'Global Solar Atlas / Global Wind Atlas (roadmap)', desc: 'Recurso solar y eólico para emplazamiento renovable.' },
    ],
    modules: {
      cadastral: {
        label: 'Catastro y límites administrativos',
        items: [
          'Provincias, cantones y parroquias — INEC (25 / 224 / 1.040)',
          'Catastro urbano Guayaquil — 606.942 predios (GAD Guayaquil)',
          'Catastro Quito — 982.108 predios + valoración AIVA (GAD Quito)',
          'Catastro rural nacional SIGTIERRAS (roadmap)',
        ],
      },
      urbanismo: {
        label: 'Urbanismo y ordenamiento territorial',
        items: [
          'PUGS Guayaquil 2023-2027 — clasificación del suelo, subzonas, PITs (GAD Guayaquil)',
          'Valoración del suelo y venta de edificabilidad — Quito',
          'Zonificación PDOT de los GADs cantonales (roadmap)',
        ],
      },
      territorial: {
        label: 'Suelo, riesgos, minería y territorios',
        items: [
          'Catastro minero nacional ARCERNNR — 15.430 concesiones con estado de trámite y mineral',
          'Aptitud agrícola, cobertura/uso de la tierra y geopedología MAG 1:25k',
          'Riesgos: peligro volcánico (14 volcanes), fallas activas GEM, factor Z sísmico NEC, tsunami',
          'Geología IIGE — cartas geológicas 1:100k y geositios',
          'Territorios indígenas — 639 polígonos (fuente regional RAISG)',
          'Recurso solar/eólico para site selection (roadmap)',
        ],
      },
      infrastructure: {
        label: 'Infraestructura y red eléctrica',
        items: [
          'Red eléctrica de transmisión SNI — 92 subestaciones + líneas 230/138 kV (CELEC)',
          'Red de distribución CNEL — subestaciones, subtransmisión, tramos MT y transformadores',
          'Expansión PME 2024-2033 — subestaciones y líneas proyectadas + demanda por subestación',
          'Red vial estatal CONGOPE + red vial completa IGM 1:50k',
          'Ferrocarril, aeropuertos y pistas de aterrizaje IGM',
          'Hidrocarburos — bloques petroleros + ductos SOTE/OCP/poliductos (mirrors cuasi-oficiales)',
        ],
      },
      environment: {
        label: 'Ambiente y áreas protegidas',
        items: [
          'SNAP — áreas protegidas continentales (81) + Galápagos (3)',
          'Bosque y Vegetación Protectora (175), Patrimonio Forestal del Estado (28)',
          'Socio Bosque (2.551), Ramsar (19), reservas de biósfera (8), OMEC (7)',
          'Zonas intangibles Tagaeri-Taromenane (2) y corredores de conectividad',
          'Mapa de ecosistemas (91.347) + páramos y áreas prioritarias de restauración',
        ],
      },
      hydrography: {
        label: 'Hidrografía y aguas',
        items: [
          'Demarcaciones hidrográficas (10) + cuencas Pfafstetter niveles 1-5 (MAATE)',
          'Régimen de inundación — 15.034 polígonos (única capa de inundación vectorial nacional)',
          'Embalses con uso hidroeléctrico (141) y presas artesanales (562)',
          'Áreas de Protección Hídrica (41) y concesiones de agua con caudal autorizado',
          'Red de drenaje IGM + ríos GeoGLOWS con caudal modelado y forecast',
        ],
      },
    },
    restrictionsHeading: 'Restricciones bloqueantes detectadas',
    restrictions: [
      { name: 'SNAP — áreas protegidas', body: 'El Sistema Nacional de Áreas Protegidas bloquea o condiciona proyectos según la categoría de manejo.' },
      { name: 'Bosque y Vegetación Protectora (BVP)', body: 'Figura de protección forestal declarada por acuerdo ministerial; condiciona el cambio de uso del suelo.' },
      { name: 'Patrimonio Forestal del Estado', body: 'Suelo forestal estatal con régimen de protección administrativa.' },
      { name: 'Zonas intangibles Tagaeri-Taromenane', body: 'Exclusión absoluta de actividad extractiva en el Yasuní (pueblos en aislamiento voluntario).' },
      { name: 'Áreas de Protección Hídrica (APH)', body: 'Figura legal de la Ley de Recursos Hídricos que protege fuentes y zonas de recarga.' },
      { name: 'Catastro minero', body: '15.430 concesiones otorgadas o en trámite — superposición que bloquea el siting de primera categoría.' },
      { name: 'Régimen de inundación', body: 'Zonas inundadas, inundables y susceptibles que afectan seguros y financiación.' },
      { name: 'Peligro volcánico y factor Z sísmico', body: '14 volcanes con peligro mapeado y zonificación sísmica NEC para diseño estructural.' },
      { name: 'Territorios indígenas', body: 'Circunscripciones y tierras comunitarias que activan la consulta previa en proyectos energéticos.' },
    ],
    regulatoryFramework: [
      { norma: 'Constitución 2008 (arts. 71-74)', scope: 'Derechos de la naturaleza — base del régimen ambiental ecuatoriano.' },
      { norma: 'Código Orgánico del Ambiente (COA)', scope: 'Marco ambiental, SNAP, licenciamiento y evaluación de impacto.' },
      { norma: 'Ley Orgánica de Recursos Hídricos', scope: 'Dominio público del agua, Áreas de Protección Hídrica y autorizaciones de uso.' },
      { norma: 'Ley de Minería', scope: 'Régimen de concesiones mineras y catastro minero nacional.' },
      { norma: 'Ley Orgánica del Servicio Público de Energía Eléctrica (LOSPEE)', scope: 'Sector eléctrico — generación, transmisión y distribución.' },
      { norma: 'COOTAD + LOOTUGS', scope: 'Ordenamiento territorial, PDOT y Planes de Uso y Gestión del Suelo (PUGS).' },
    ],
    faq: [
      { q: '¿Qué cobertura tiene GeoVis en Ecuador ahora mismo?', a: 'Cobertura inicial operativa centrada en site selection energético: restricciones ambientales del MAATE (SNAP, BVP, Patrimonio Forestal, Socio Bosque, Ramsar, APH, ecosistemas), red eléctrica de transmisión y distribución (CELEC/CNEL), hidrografía (cuencas Pfafstetter, embalses, régimen de inundación, GeoGLOWS), catastro minero ARCERNNR, geología IIGE, riesgos naturales, aptitud agrícola MAG y catastro urbano de Quito y Guayaquil.' },
      { q: '¿Por qué Ecuador es relevante para proyectos renovables?', a: 'Tras la crisis eléctrica de 2024, el gobierno abrió la inversión privada en generación y licita bloques (FV Zapotillo 1.500 MW, Santa Elena 200 MW, ciclo combinado a gas, entre otros). GeoVis cruza estas oportunidades con SNAP, catastro minero, red CNEL/CELEC y régimen de inundación para el cribado de emplazamientos.' },
      { q: '¿Incluye el catastro rural nacional?', a: 'Todavía no. El catastro rural nacional de SIGTIERRAS está pendiente de restablecer su infraestructura y figura en el roadmap. Hoy el catastro disponible es urbano (Quito y Guayaquil) y se sustituye el suelo rural con aptitud agrícola, cobertura y geopedología del MAG 1:25k.' },
      { q: '¿De dónde salen los datos eléctricos sin depender del regulador?', a: 'La red se construye con el geoportal de CNEL EP (distribución nacional), el servicio SE_SNI_CELEC (92 subestaciones de transmisión) y el plan de expansión PME 2024-2033, replicados a CDN propio. No se consume infraestructura gubernamental en runtime porque es frágil.' },
      { q: '¿Cómo participan los usuarios de acceso anticipado?', a: 'El registro da acceso a las capas ya operativas, prioridad en la integración de nuevas fuentes (catastro rural, recurso solar/eólico, PDOT) y precios preferentes cuando se complete la cobertura nacional.' },
    ],
    glossary: [
      { term: 'SNAP', definition: 'Sistema Nacional de Áreas Protegidas del Ecuador (continental + Galápagos).' },
      { term: 'MAATE', definition: 'Ministerio del Ambiente, Agua y Transición Ecológica.' },
      { term: 'ARCERNNR', definition: 'Agencia de Regulación y Control de Energía y Recursos Naturales No Renovables.' },
      { term: 'BVP', definition: 'Bosque y Vegetación Protectora — figura de protección forestal por acuerdo ministerial.' },
      { term: 'APH', definition: 'Área de Protección Hídrica — figura de la Ley Orgánica de Recursos Hídricos.' },
      { term: 'SNI', definition: 'Sistema Nacional Interconectado — red eléctrica de transmisión del Ecuador.' },
      { term: 'PUGS', definition: 'Plan de Uso y Gestión del Suelo — instrumento municipal de la LOOTUGS.' },
      { term: 'PDOT', definition: 'Plan de Desarrollo y Ordenamiento Territorial de los GAD.' },
      { term: 'Factor Z', definition: 'Coeficiente de zonificación sísmica de la norma NEC-SE-DS.' },
      { term: 'Pfafstetter', definition: 'Sistema jerárquico de codificación de cuencas hidrográficas.' },
    ],
    legalReference: 'Marco aplicable: Constitución de la República 2008 (derechos de la naturaleza), Código Orgánico del Ambiente, Ley Orgánica de Recursos Hídricos, Ley de Minería, Ley Orgánica del Servicio Público de Energía Eléctrica (LOSPEE), COOTAD y Ley Orgánica de Ordenamiento Territorial, Uso y Gestión del Suelo (LOOTUGS).',
    ctaPrimary: 'Apuntarme al acceso anticipado',
    ctaSecondary: 'Hablar con el equipo',
    signupUrl: 'https://aplicacion.geovis.es/auth/signup?source=landing_ec',
  },

  // ─────────────────────────────────────────────────────────────────────────
  za: {
    code: 'za',
    hreflang: 'en-ZA',
    path: '/za',
    flag: '🇿🇦',
    countryName: 'South Africa',
    countryNative: 'South Africa',
    languageLabel: 'English',
    coverageStatus: 'operativa',
    modulesCount: 56,
    title: 'GeoVis South Africa · GIS Viewer for Land Analysis and Project Feasibility | Surveyor-General Cadastre + REIPPPP + EIA Screening',
    description:
      'GIS viewer for land analysis in South Africa: Chief Surveyor-General cadastre (7.2M erven), Eskom power grid and per-substation GCCA connection capacity, REIPPPP projects, REDZ zones and EGI corridors, DFFE Environmental Screening Tool sensitivity, SANBI biodiversity (CBA/ESA, protected areas, VEGMAP), SAHRA heritage, DWS hydrography and CGS geology. 56+ official layers on a single map.',
    badge: '7-day free trial · No card required',
    h1Lead: 'Land analysis and',
    h1Highlight: 'project feasibility',
    h1Tail: 'in South Africa',
    subtitle:
      'GIS viewer with Surveyor-General cadastre, Eskom power grid and GCCA connection capacity, REIPPPP projects, REDZ zones, DFFE Screening environmental sensitivity, SANBI biodiversity, SAHRA heritage, DWS hydrography and CGS geology. Built for utility-scale renewables, land due diligence, environmental consulting, engineering and mining.',
    coverageBlurb:
      'Fully operational national coverage with 56+ official layers across five canonical groups. The viewer unifies the Chief Surveyor-General cadastre (7.2M erven + farm parcels), the Eskom transmission network with published per-substation connection capacity (GCCA), the REIPPPP pipeline of awarded renewable projects, REDZ zones and EGI corridors, pre-calculated environmental sensitivity from the DFFE Environmental Screening Tool, SANBI BGIS biodiversity data, SAHRA-graded heritage sites, DWS hydrography and Council for Geoscience geology, with professional overlay and export tools. World-class solar and wind resource data is included as market context.',
    sectors: [
      { name: 'Renewables (utility-scale solar PV · wind)', body: 'World-class solar/wind resource, per-substation GCCA connection capacity, REDZ zones and EGI corridors, DFFE sensitivity differentiated for solar PV/wind.' },
      { name: 'Land due diligence', body: 'Screening of land portfolios and identification of critical constraints before closing options.' },
      { name: 'Environmental consulting', body: 'Overlay with CBA/ESA, protected areas, EIA sensitivity and bird data for Basic Assessment and Scoping+EIR processes.' },
      { name: 'Engineering and infrastructure', body: 'Eskom grid, NGI roads, railways, aviation restrictions, defence and radar.' },
      { name: 'Mining and geology', body: 'CGS surface geology, faults, industrial minerals, mines and induced seismicity.' },
      { name: 'Cadastre and tenure', body: 'Surveyor-General erven, farm parcels, Ingonyama Trust, traditional authorities and homelands.' },
      { name: 'Agriculture', body: 'National Land Capability, protected agricultural areas and land use.' },
      { name: 'Public administration', body: 'Technical evaluation of initiatives with full traceability by layer and official source.' },
    ],
    sourcesHeading: 'Official data sources integrated',
    sources: [
      { name: 'Chief Surveyor-General (CSG/DALRRD)', desc: 'Erf cadastre (7.2M parcels, SG21KEY code) and farm parcels.' },
      { name: 'Eskom / NTCSA — GCCA', desc: 'Transmission lines, MTS substations with connection capacity (MVA) and supply areas.' },
      { name: 'NTCSA — REIPPPP', desc: 'Awarded renewable projects (bid windows 1-4b) with technology and MW.' },
      { name: 'DFFE — Environmental Screening Tool', desc: 'Pre-calculated EIA sensitivity (heritage, birds, defence, aviation), differentiated for solar PV/wind.' },
      { name: 'DFFE — REDZ and EGI corridors', desc: 'Renewable Energy Development Zones and strategic transmission and gas corridors.' },
      { name: 'SANBI BGIS', desc: 'CBA/ESA, protected areas (SAPAD), MPA, VEGMAP, NBA, RLE, IBA and strategic water source areas (SWSA).' },
      { name: 'SAHRA', desc: 'Graded heritage sites (I/II/IIIa-c) and protection buffers.' },
      { name: 'DWS — Department of Water and Sanitation', desc: 'Water Management Areas, catchments, NFEPA/50k rivers and dams.' },
      { name: 'Council for Geoscience (CGS)', desc: 'Surface geology 1:1M, faults, industrial minerals and seismicity.' },
      { name: 'Municipal Demarcation Board (MDB)', desc: 'Provinces, districts and local/metro municipalities.' },
      { name: 'Metro municipalities (CoCT, eThekwini, Tshwane…)', desc: 'Wall-to-wall zoning by capital/metro at parcel level.' },
    ],
    modules: {
      cadastral: {
        label: 'Cadastre and administrative division',
        items: [
          'Erf cadastre — Chief Surveyor-General (7.2M parcels, SG21KEY code)',
          'MDB farm parcels — Farm Location Finder (100,508)',
          'Provinces (9), district municipalities (52) and local/metro municipalities (214) — MDB',
          'Communal tenure — Ingonyama Trust, traditional authorities, homelands',
        ],
      },
      urbanismo: {
        label: 'Planning and land use',
        items: [
          'Wall-to-wall zoning by metro/capital (Cape Town, eThekwini, Tshwane, Mangaung, Polokwane)',
          'National settlements and towns (29,512)',
          'Land Capability — national agricultural land capability',
          'Protected agricultural areas (557)',
        ],
      },
      territorial: {
        label: 'Renewable energy and development zones',
        items: [
          'Awarded REIPPPP projects — bid windows 1-4b (98, with technology and MW)',
          'REDZ — Renewable Energy Development Zones (11)',
          'Strategic EGI transmission corridors (7) and gas pipeline corridors (9)',
          'DFFE Screening EIA sensitivity — heritage, birds and defence, differentiated for solar PV/wind',
        ],
      },
      infrastructure: {
        label: 'Infrastructure',
        items: [
          'Eskom grid — existing transmission lines (348) + planned (96)',
          'MTS substations (198) with connection capacity (MVA) + GCCA and supply areas',
          'NGI roads (9,477) and railways (33,905)',
          'SACAA aviation restrictions — airspace, radar and aerodromes (634)',
          'Defence and military sites with buffers (1,251)',
          'Telecoms, broadcasting and the SKA radio-astronomy exclusion zone',
        ],
      },
      environment: {
        label: 'Environment and heritage',
        items: [
          'CBA/ESA — Critical Biodiversity Areas and Ecological Support Areas (SANBI)',
          'Terrestrial protected areas SAPAD (1,562) and marine protected areas MPA (41)',
          'VEGMAP (48,732), NBA threat status, RLE 2021 and threatened ecosystems',
          'Important Bird Areas IBA (175) and World Heritage Sites (9)',
          'SAHRA-graded heritage sites (55,167 sites, I/II/IIIa-c)',
        ],
      },
      hydrography: {
        label: 'Hydrography and water',
        items: [
          'DWS Water Management Areas (8) and Primary→Quaternary catchments',
          'NFEPA rivers (797) and DWS 50k river network (perennial + non-perennial)',
          'Major DWS dams (615) and reservoirs',
          'Strategic surface water source areas (SWSA, 22) and groundwater source areas (57)',
        ],
      },
    },
    restrictionsHeading: 'Blocking restrictions detected',
    restrictions: [
      { name: '"Very High" EIA sensitivity (DFFE Screening)', body: 'Pre-calculated EIA buffers that escalate a project from a Basic Assessment to a full Scoping+EIR process.' },
      { name: 'CBA/ESA and protected areas', body: 'Critical Biodiversity Areas, SAPAD and MPA either block or place conditions on development, depending on the management plan.' },
      { name: 'SAHRA heritage grading', body: 'Grade I-IIIc sites require a Heritage Impact Assessment and a permit under the NHRA.' },
      { name: 'Bird sensitivity (wind)', body: 'Nesting sites and vultures carry extended buffers — the main factor differentiating wind feasibility from solar PV.' },
      { name: 'Eskom connection capacity (GCCA)', body: 'Without available MVA at the nearest substation (CLNLimit), the project cannot connect to the grid.' },
      { name: 'SKA exclusion zone (Karoo)', body: 'Radio-astronomy restrictions on telecommunications and development within the Astronomy Geographic Advantage Area.' },
      { name: 'Aviation and defence restrictions', body: 'Restricted airspace, radar buffers (15/35 km) and military sites limit height and land occupation.' },
      { name: 'Communal tenure (Ingonyama Trust / traditional authorities)', body: 'Land under traditional tenure requires negotiation with the relevant authority and increases acquisition risk.' },
    ],
    regulatoryFramework: [
      { norma: 'NEMA — Act 107 of 1998', scope: 'National Environmental Management Act — environmental impact assessment (EIA).' },
      { norma: 'NEMPAA — Act 57 of 2003', scope: 'Protected Areas Act — terrestrial and marine protected areas.' },
      { norma: 'SPLUMA — Act 16 of 2013', scope: 'Spatial Planning and Land Use Management — municipal planning and zoning.' },
      { norma: 'NHRA — Act 25 of 1999', scope: 'National Heritage Resources Act — heritage and SAHRA grading.' },
      { norma: 'National Water Act — Act 36 of 1998', scope: 'Water resources, use authorisations and riparian zones.' },
      { norma: 'Electricity Regulation Act — Act 4 of 2006', scope: 'Generation, grid connection and the REIPPPP programme.' },
    ],
    faq: [
      { q: 'Does GeoVis replace QGIS or ArcGIS?', a: 'No. GeoVis is a screening and due diligence viewer with South Africa\'s official layers pre-loaded and kept in sync. QGIS and ArcGIS are still needed for advanced geoprocessing, heavy raster work or custom ETL. Most teams use GeoVis for fast screening and only turn to QGIS for the final detail.' },
      { q: 'Where does the grid connection capacity data come from?', a: 'From the Grid Capacity Connection Assessments (GCCA) published by Eskom/NTCSA: each MTS substation shows the available connection MVA (CLNLimit), together with existing and planned transmission lines and supply areas.' },
      { q: 'How does it help with a renewable project\'s EIA?', a: 'The DFFE Environmental Screening Tool provides sensitivity layers with pre-calculated buffers, differentiated for solar PV and wind (heritage, birds, defence, aviation). Overlaying the project boundary lets you anticipate whether it will require a Basic Assessment or a full Scoping+EIR process, and whether it falls within a REDZ zone with fast-tracked processing.' },
      { q: 'Does it include the full cadastre?', a: 'Yes. It integrates the Chief Surveyor-General\'s 7.2 million erven with the official SG21KEY parcel code, plus MDB farm parcels. Because of its size, the cadastre is served at high zoom levels (from parcel level upward).' },
      { q: 'Is it useful for due diligence on communal land?', a: 'Yes. It includes the Ingonyama Trust parcel layer in KwaZulu-Natal, traditional authorities and the former homelands — key layers for assessing acquisition risk on land under traditional tenure.' },
      { q: 'How many official layers are integrated?', a: 'More than 56 layers across five canonical groups (Cadastre, Planning, Land/Energy, Infrastructure and Environment/Hydrography), sourced from the Surveyor-General, Eskom/NTCSA, DFFE, SANBI, SAHRA, DWS, CGS and MDB. Solar and wind resource data is included for market context.' },
    ],
    glossary: [
      { term: 'REIPPPP', definition: 'Renewable Energy Independent Power Producer Procurement Programme — South Africa\'s renewable energy procurement programme.' },
      { term: 'REDZ', definition: 'Renewable Energy Development Zones — zones with fast-tracked environmental processing (Basic Assessment).' },
      { term: 'GCCA', definition: 'Eskom\'s Grid Capacity / Connection Assessment — available connection capacity per substation (MVA).' },
      { term: 'CBA / ESA', definition: 'Critical Biodiversity Areas / Ecological Support Areas — SANBI\'s conservation priority areas.' },
      { term: 'Screening EIA (DFFE)', definition: 'Environmental Screening Tool with pre-calculated environmental sensitivity by technology.' },
      { term: 'SG21KEY', definition: 'The Chief Surveyor-General\'s official parcel code identifying each erf.' },
      { term: 'SAHRA', definition: 'South African Heritage Resources Agency — grades heritage sites (I/II/IIIa-c).' },
      { term: 'SKA', definition: 'Square Kilometre Array — radio-astronomy exclusion zone in the Karoo.' },
    ],
    legalReference: 'Applicable framework: NEMA (Act 107 of 1998, EIA), NEMPAA (Act 57 of 2003, protected areas), SPLUMA (Act 16 of 2013, planning and zoning), NHRA (Act 25 of 1999, heritage), National Water Act (Act 36 of 1998, water), Electricity Regulation Act (Act 4 of 2006, grid and REIPPPP).',
    ctaPrimary: 'Start for free',
    ctaSecondary: 'Request a demo',
    signupUrl: 'https://aplicacion.geovis.es/auth/signup?source=landing_za',
  },
};

export const LOCALE_ORDER = ['es', 'pt', 'it', 'fr', 'ro', 'cl', 'pe', 'no', 'pl', 'cz', 'uk', 'mx', 'co', 'ec', 'za'];

export function getLocale(code) {
  return LOCALES[code] || LOCALES.es;
}

export function getAllHreflangs() {
  return LOCALE_ORDER.map((code) => {
    const l = LOCALES[code];
    return { hreflang: l.hreflang, href: `${BASE_URL}${l.path}` };
  });
}

export function getHreflangsForUrl() {
  const alts = getAllHreflangs();
  alts.push({ hreflang: 'x-default', href: `${BASE_URL}/` });
  return alts;
}

export { BASE_URL };
